import { AlertCircle, ArrowRight, BarChart3, Check, FileText, Play, Upload, Zap, Eye, RefreshCw, Edit3, Save, X, Download, Image, Code, Minus, Plus, ShieldCheck, Square } from 'lucide-react';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import toast from 'react-hot-toast';
import { tokenStorage } from '../utils/tokenStorage';
import { useQueryClient } from '@tanstack/react-query';
import api from '../api';
import { getApiErrorMessage, showApiError, showApiErrorAsync } from '../utils/apiError';
import { countMapSteps, resolveTotalSteps, summariseValidation, validationDetailLine, validationPercent, validationStepLabel, type ValidationHeadline } from '../utils/validationProgress';
import { pollVerificationJob, describeProgress } from '../services/verificationJobPolling';
import ClearCacheButton from '../components/ProcessMaps/ClearCache';
import ProcessMapLoader from '../components/ProcessMaps/ProcessMapLoader';
import SwimlanesVisualization, { type ExportFunctions } from '../components/ProcessMaps/SwimlanesVisualization';
import DrawIOClientEditor from '../components/ProcessMaps/DrawIOClientEditor';
import FrictionLegend from '../components/ProcessMaps/FrictionLegend';
import FrictionHeatmapChart from '../components/ProcessMaps/FrictionHeatmapChart';
import useFrictionMetrics from '../hooks/useFrictionMetrics';
import ValidationCommandCenter from '../components/ProcessMaps/ValidationCommandCenter';
import ProcessMapStatistics from '../components/ProcessMaps/ProcessMapStatistics';
import ToolbarDropdown from '../components/ProcessMaps/ToolbarDropdown';
import ProcessMapValidationBar, { type ValidationStepDef, type ValidationStepStatus } from '../components/ProcessMaps/ProcessMapValidationBar';
import ConfirmDialog, { type ConfirmRequest } from '../components/ProcessMaps/ConfirmDialog';
import HelpTooltip from '../components/HelpTooltip';
import AccuracyGateBadge from '../components/Verification/AccuracyGateBadge';
import { isAccuracyGateDone, accuracyGateHeadline, mergeFindingStatuses, sourceHasDiamonds, stakeholderNamesFromMap, type FindingStatus } from '../utils/accuracyGate';
import { applyAccuracyGateFinding, applyManualDecisionEdit } from '../utils/applyAccuracyGateFinding';
import VerificationPanel from '../components/Verification/VerificationPanel';
import { useProject } from '../contexts/ProjectContext';
import { useAuth } from '../contexts/AuthContext';
import { procesMapUpdateManager } from '../App';
import SmartRouterBanner from '../components/SmartRouter/SmartRouterBanner';
import OrchestratorProjectMismatchBanner from '../components/SmartRouter/OrchestratorProjectMismatchBanner';
import { useOrchestratorContext } from '../hooks/useOrchestratorContext';
import DownstreamStalenessBanner, { DownstreamStaleness } from '../components/Analysis/DownstreamStalenessBanner';



interface ProcessMap {
  id: number;
  level: number;
  map_data: any;
  audit_trail?: any;
  generation_metadata?: any;
  data_lineage?: any;
  title: string;
  complexity_score: number;
  created_at: string;
  version_number?: number;
  edited_by?: string;
  updated_at: string;
  exception_coverage_score?: number;
  total_exceptions?: number;
  escalation_hierarchy?: any;
  process_name: string;
  process_id: string;
  downstream_staleness?: DownstreamStaleness;
}

interface AnalysisProgress {
  progress: number;
  completed: number;
  failed: number;
  pending: number;
  total: number;
}

/**
 * Approval state for the current project's process map. Owned by SOPBuilder
 * (it drives step-gating for the whole SOP flow), passed down so the Approve
 * control can live inside the validation bar instead of in a separate banner.
 */
export interface ProcessMapApprovalInfo {
  id: number;
  level: number;
  validation_status?: string | null;
  approved_at?: string | null;
}

interface ProcessMapsProps {
  approvalInfo?: ProcessMapApprovalInfo | null;
  approving?: boolean;
  onApprove?: () => void;
  onRevokeApproval?: () => void;
  /** Saving an edit revokes approval server-side; the owner has to re-read it. */
  onApprovalStateChanged?: () => void;
}

/**
 * The level tab the user last looked at, per project.
 *
 * Without this the screen opens on Level 1 every time and then jumps to
 * whichever level happens to have a map, so a consultant working in L2 is moved
 * to L3 by every page reload — and a level they opened deliberately to generate
 * is treated as a mistake and corrected away. Remembering the choice also lets
 * the first paint land on the right tab instead of flashing "No Process Map for
 * Level 1" until the maps arrive.
 *
 * sessionStorage, not localStorage: this is where you were a moment ago, not a
 * preference worth carrying into next week. Reads and writes are guarded
 * because Safari's private mode throws on access.
 */
const LAST_LEVEL_KEY = 'jarvis_process_map_level';

const readLastViewedLevel = (projectId?: number | string | null): number | null => {
  if (!projectId) return null;
  try {
    const stored = Number(sessionStorage.getItem(`${LAST_LEVEL_KEY}:${projectId}`));
    return stored >= 1 && stored <= 4 ? stored : null;
  } catch {
    return null;
  }
};

const writeLastViewedLevel = (projectId: number | string | null | undefined, level: number) => {
  if (!projectId) return;
  try {
    sessionStorage.setItem(`${LAST_LEVEL_KEY}:${projectId}`, String(level));
  } catch {
    // Storage unavailable — the session just falls back to the resolved default.
  }
};

/**
 * Which level tab to show for a set of maps.
 *
 * Data-driven in both branches, so it is right whichever levels happen to
 * exist: stay where the user is if that level has a map, otherwise return to
 * the level they last viewed if that one does, otherwise the highest level that
 * has a map. Returns null when nothing has been generated yet — there is no
 * better tab to offer, so the caller leaves the selection alone.
 */
const resolveLevelToShow = (
  maps: Array<{ level: number }>,
  currentLevel: number,
  lastViewedLevel: number | null,
): number | null => {
  if (!maps.length) return null;
  const hasMap = (level: number | null) => level !== null && maps.some(map => map.level === level);

  if (hasMap(currentLevel)) return currentLevel;
  if (hasMap(lastViewedLevel)) return lastViewedLevel;
  return maps.reduce((best, map) => (map.level > best.level ? map : best)).level;
};

const ProcessMaps: React.FC<ProcessMapsProps> = ({
  approvalInfo = null,
  approving = false,
  onApprove,
  onRevokeApproval,
  onApprovalStateChanged,
}) => {

  const { currentProject } = useProject();
  const { user } = useAuth();
  const queryClient = useQueryClient(); 
  const orchestratorCtx = useOrchestratorContext();
  // Opens where the user left off; the maps decide the rest once they arrive.
  const [selectedLevel, setSelectedLevel] = useState(() => readLastViewedLevel(currentProject?.id) ?? 1);
  // Which project we have already auto-corrected the level for (see loadProcessMaps).
  const autoLevelPickedForProject = useRef<number | null>(null);

  const [confirmRequest, setConfirmRequest] = useState<ConfirmRequest | null>(null);

  const selectLevel = useCallback((level: number) => {
    setSelectedLevel(level);
    writeLastViewedLevel(currentProject?.id, level);
  }, [currentProject?.id]);

  // Switching projects carries the old project's level over, and it may not
  // exist there. Fall back to where the user last was in the project being
  // opened, then let the load resolve it against what actually has a map.
  const lastSeenProjectId = useRef<number | null>(null);
  useEffect(() => {
    const projectId = currentProject?.id ?? null;
    if (projectId === lastSeenProjectId.current) return;
    lastSeenProjectId.current = projectId;
    if (projectId) setSelectedLevel(readLastViewedLevel(projectId) ?? 1);
  }, [currentProject?.id]);

  /**
   * This screen polls on several timers, and under load a single response can
   * take twenty seconds. The timers are cleared on unmount but the requests they
   * already fired were not, so they kept holding the browser's six connections
   * per host after the user navigated away. Whatever screen came next queued
   * behind them: SOP Builder's approval call hit its own 30s ceiling and the
   * wizard re-locked itself while the documents panel sat on "Loading
   * documents...". Opening a fresh tab looked like a fix only because a new tab
   * gets a new connection pool.
   *
   * The controller is created inside the effect, not at ref init, so a
   * StrictMode double-mount cannot leave an already-aborted controller behind.
   */
  const pollAbortRef = useRef<AbortController | null>(null);
  useEffect(() => {
    const controller = new AbortController();
    pollAbortRef.current = controller;
    return () => controller.abort();
  }, []);
  const wasAborted = (error: any) =>
    error?.code === 'ERR_CANCELED' || error?.name === 'CanceledError';
  const [loading, setLoading] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [showVerificationPanel, setShowVerificationPanel] = useState(false);
  const [isEditMode, setIsEditMode] = useState(false);
  const [zoomLevel, setZoomLevel] = useState(75);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const [editedMapData, setEditedMapData] = useState<any>(null);
  const [savingChanges, setSavingChanges] = useState(false);
  // Step ids the user has marked for deletion in edit mode; applied on Save.
  const [pendingDeleteIds, setPendingDeleteIds] = useState<string[]>([]);
  const [findingSessionStatus, setFindingSessionStatus] = useState<Record<string, FindingStatus>>({});
  const [enhancedProcesses, setEnhancedProcesses] = useState<any[]>([]);
  const [loadingRoleAssignments, setLoadingRoleAssignments] = useState(false);
  const loadingRef = useRef(false);
  const canvasRef = useRef<HTMLDivElement>(null);
  const [allProcessMaps, setAllProcessMaps] = useState<ProcessMap[]>([]);

  // No longer drives any UI (the "Audit Info Ready" chip was removed with the
  // in-UI audit panel). Retained only because the polling effect and the
  // SwimlanesVisualization prop still reference it; safe to delete together.
  const [auditEnhancementStatus, setAuditEnhancementStatus] = useState<{
    status: 'not_started' | 'in_progress' | 'completed' | 'failed' | 'deferred';
    progress: number;
    enhanced_steps_count: number;
    total_steps: number;
    error?: string;
  } | null>(null);

  const [analysisProgress, setAnalysisProgress] = useState<AnalysisProgress | null>(null);
  const [analysisResults, setAnalysisResults] = useState<any>(null);
  const [isAnalyzing, setIsAnalyzing] = useState(false);

  const [analysisStartTime, setAnalysisStartTime] = useState<number | null>(null);
  const [analysisTimedOut, setAnalysisTimedOut] = useState(false);
  const [hasDocuments, setHasDocuments] = useState<boolean>(false);
  const [hasAnalysis, setHasAnalysis] = useState<boolean>(false);
  const [hasSynthesis, setHasSynthesis] = useState<boolean>(false);
  const [documentCount, setDocumentCount] = useState<number>(0);
  const [prereqsLoaded, setPrereqsLoaded] = useState<boolean>(false);

  const [loaderOperation, setLoaderOperation] = useState<'generating' | 'switching' | 'loading' | 'regenerating' | 'syncing'>('loading');
  const [loaderProgress, setLoaderProgress] = useState(0);
  const [loaderStage, setLoaderStage] = useState('');
  const [showLoader, setShowLoader] = useState(false);
  const [cancellingGeneration, setCancellingGeneration] = useState(false);
  const mapCancelRequestedRef = useRef(false);

  const [currentMap, setCurrentMap] = useState<ProcessMap | null>(null);

  // Professional diagram state
  const [diagramMode, setDiagramMode] = useState<'interactive' | 'professional'>('interactive');

  // Validation Command Center state
  const [showCommandCenter, setShowCommandCenter] = useState(false);
  const [validationId, setValidationId] = useState<number | null>(null);
  const [validationStatus, setValidationStatus] = useState<string | null>(null);
  const [validating, setValidating] = useState(false);
  const [validationProgress, setValidationProgress] = useState<{
    completed_steps?: number;
    total_steps?: number;
    estimated_progress?: number;
    started_at?: string | null;
  } | null>(null);
  // The in-page Validation Report panel was removed in favour of an Export menu
  // entry, so the headline figures it used to show are kept here and surfaced as
  // a compact chip on the validation bar.
  const [validationSummary, setValidationSummary] = useState<ValidationHeadline | null>(null);
  const validationPollingRef = useRef<NodeJS.Timeout | null>(null);
  const [mapRefreshCounter, setMapRefreshCounter] = useState(0); // ✅ FIX: Force re-render after validation updates

  // Gap Statistics Panel state
  const [showGapStatistics, setShowGapStatistics] = useState(false);

  // Export functions from SwimlanesVisualization
  const [exportFns, setExportFns] = useState<ExportFunctions | null>(null);

  const [isVerifyingMap, setIsVerifyingMap] = useState(false);
  const [exportingAudit, setExportingAudit] = useState(false);
  const [exportingValidationReport, setExportingValidationReport] = useState(false);

  // Friction Heatmap state
  const [showFrictionHeatmap, setShowFrictionHeatmap] = useState(false);
  const [showHeatmapChart, setShowHeatmapChart] = useState(false);
  const {
    frictionData,
    frictionMap,
    isLoading: frictionLoading,
    isEstimating: frictionEstimating,
    fetchFrictionData,
    estimateFriction,
    clearFrictionData
  } = useFrictionMetrics();

  const checkAuditEnhancementStatus = useCallback(async () => {
    if (!currentProject?.id || selectedLevel !== 3) return;

    try {
      const token = localStorage.getItem('jarvis_access_token');
      const response = await api.get(`/api/analysis/audit-enhancement-status/${currentProject.id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      const data = response.data;
      setAuditEnhancementStatus({
        status: data.enhancement_status,
        progress: data.enhancement_progress || 0,
        enhanced_steps_count: data.enhanced_steps_count || 0,
        total_steps: data.total_steps || 0,
        error: data.enhancement_error
      });

      // Show status if enhancement is in progress or completed
      if (data.enhancement_status === 'in_progress' || data.enhancement_status === 'completed') {
        // Status will be visible via auditEnhancementStatus state
      }

    } catch (error) {
      console.error('Failed to check audit enhancement status:', error);
    }
  }, [currentProject?.id, selectedLevel]);

  // Fetch validation status for Command Center button
  const fetchValidationStatus = useCallback(async () => {
    if (!currentProject?.id || !currentMap?.id) {
      setValidationId(null);
      setValidationStatus(null);
      setValidationSummary(null);
      return;
    }

    try {
      const response = await api.get(`/api/validation/process-map/${currentMap.id}/validation-status`);
      const status = response.data?.status || null;
      setValidationStatus(status);
      setValidationSummary(summariseValidation(response.data));

      // Set progress data
      if (response.data) {
        setValidationProgress({
          completed_steps: response.data.completed_steps,
          total_steps: response.data.total_steps,
          estimated_progress: response.data.estimated_progress
        });
      }

      if (response.data?.validation_id) {
        if (status === 'COMPLETED') {
          setValidationId(response.data.validation_id);
          setValidating(false);
        } else if (status === 'IN_PROGRESS') {
          setValidationId(null);
          setValidating(true);
        } else {
          setValidationId(null);
          setValidating(false);
        }
      } else {
        setValidationId(null);
        setValidating(false);
      }
    } catch (error) {
      setValidationId(null);
      setValidationStatus(null);
      setValidationSummary(null);
      setValidating(false);
      setValidationProgress(null);
    }
  }, [currentProject?.id, currentMap?.id]);

  // Start polling for validation progress
  const startValidationPolling = useCallback(() => {
    // Clear any existing polling
    if (validationPollingRef.current) {
      clearInterval(validationPollingRef.current);
    }

    validationPollingRef.current = setInterval(async () => {
      if (!currentMap?.id) return;

      try {
        const statusRes = await api.get(`/api/validation/process-map/${currentMap.id}/validation-status`, {
          signal: pollAbortRef.current?.signal,
        });
        setValidationStatus(statusRes.data?.status || null);
        setValidationSummary(summariseValidation(statusRes.data));
        setValidationProgress(prev => ({
          completed_steps: statusRes.data?.completed_steps,
          // The poll omits total_steps until a validation row exists. Keep the
          // count we already know from the loaded map so the banner never
          // regresses to "0 process steps" mid-run.
          total_steps: statusRes.data?.total_steps ?? prev?.total_steps,
          estimated_progress: statusRes.data?.estimated_progress,
          started_at: statusRes.data?.started_at ?? prev?.started_at ?? null
        }));

        if (statusRes.data?.status === 'COMPLETED') {
          if (validationPollingRef.current) {
            clearInterval(validationPollingRef.current);
            validationPollingRef.current = null;
          }
          setValidationId(statusRes.data.validation_id);
          setValidating(false);

          // ✅ Force re-render of visualization - this will trigger reload via key change
          setMapRefreshCounter(prev => prev + 1);

          toast.success('Validation completed');
          setShowCommandCenter(true);
        } else if (statusRes.data?.status === 'FAILED') {
          if (validationPollingRef.current) {
            clearInterval(validationPollingRef.current);
            validationPollingRef.current = null;
          }
          setValidating(false);
          toast.error('Validation failed');
        }
      } catch (err) {
        // Continue polling on transient errors
      }
    }, 3000);

    // Timeout after 10 minutes
    setTimeout(() => {
      if (validationPollingRef.current) {
        clearInterval(validationPollingRef.current);
        validationPollingRef.current = null;
      }
      setValidating(false);
    }, 600000);
  }, [currentMap?.id]);

  // Start validation from Command Center button
  const startValidationFromButton = async () => {
    if (!currentProject?.id || !currentMap?.id) return;

    if (currentMap?.map_data?.metadata?.preserve_mode) {
      toast.error('Validation is not available for preserved process maps. Generate an AI process map to use validation.');
      return;
    }

    // Seed the banner from the map already on screen so it names the real step
    // count from the first frame instead of waiting three seconds for a poll
    // that does not carry one until the validation row exists.
    setValidating(true);
    setValidationProgress({
      total_steps: countMapSteps(currentMap.map_data),
      started_at: new Date().toISOString()
    });
    try {
      const startRes = await api.post(`/api/validation/process-map/${currentMap.id}/validate`, {
        project_id: currentProject.id
      });

      // The backend re-resolves stale map ids (a Regenerate — possibly by
      // another collaborator — swaps the map row and its id). If that
      // happened, refresh the map list so we poll and render the live map.
      if (startRes.data?.resolved_from_stale_id) {
        toast.success('Process map was regenerated — validation started on the current map');
        await loadProcessMaps();
      } else {
        toast.success('Validation started');
      }

      // ✅ Force re-render to show validation started state
      setMapRefreshCounter(prev => prev + 1);

      startValidationPolling();

    } catch (error: any) {
      // Handle 409 Conflict - validation already in progress
      if (error.response?.status === 409) {
        toast.success('Validation already in progress');
        startValidationPolling();
      } else if (error.response?.status === 404) {
        // Map id no longer exists and the backend couldn't re-resolve it —
        // reload so the UI stops holding a dead id.
        console.error('Failed to start validation (stale map id):', error);
        toast.error('This process map is out of date — refreshing the view, please try again');
        await loadProcessMaps();
        setValidating(false);
      } else {
        console.error('Failed to start validation:', error);
        showApiError(error, 'Failed to start validation');
        setValidating(false);
      }
    }
  };

  // Cleanup polling on unmount
  useEffect(() => {
    return () => {
      if (validationPollingRef.current) {
        clearInterval(validationPollingRef.current);
      }
    };
  }, []);

  // Fetch validation status when map changes
  useEffect(() => {
    fetchValidationStatus();
  }, [fetchValidationStatus]);

  const checkProjectPrerequisites = useCallback(async () => {
    // No project yet (e.g. ProjectContext still hydrating from localStorage, or
    // the user landed on the standalone /app/process-maps route before a project
    // was selected). We must still flip prereqsLoaded, otherwise the render gate
    // in renderProcessMapVisualization() (loading || !prereqsLoaded) spins
    // forever and the map never appears. Treat "no project" as "no prerequisites
    // met" rather than "still loading".
    if (!currentProject?.id) {
      setHasDocuments(false);
      setHasAnalysis(false);
      setHasSynthesis(false);
      setDocumentCount(0);
      setPrereqsLoaded(true);
      return;
    }

    try {
      const token = localStorage.getItem('jarvis_access_token');

      // Get documents
      const documentsResponse = await api.get(`/api/documents/project/${currentProject.id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const documents = documentsResponse.data.documents || [];
      setHasDocuments(documents.length > 0);
      setDocumentCount(documents.length);

      // Get analysis results
      const analysisResponse = await api.get(`/api/analysis/results/${currentProject.id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const analysisResults = analysisResponse.data;
      const analyzedDocuments = analysisResults?.document_analyses || [];

      // Check if ALL current documents are analyzed using TWO methods:
      // Method 1: Check if document analysis records exist for all documents
      const analyzedDocumentIds = new Set(analyzedDocuments.map((a: any) => a.Document?.id).filter(Boolean));
      const hasAnalysisRecords = documents.length > 0 && documents.every((doc: any) => analyzedDocumentIds.has(doc.id));

      // Method 2: Check if all documents have analysis_status = 'completed'
      const hasCompletedStatus = documents.length > 0 && documents.every((doc: any) => doc.analysis_status === 'completed');

      // Use EITHER method - if documents have completed status OR have analysis records
      const allDocumentsAnalyzed = documents.length > 0 && (hasAnalysisRecords || hasCompletedStatus);

      setHasAnalysis(allDocumentsAnalyzed);

      // Check process synthesis - but only if we have analysis for all documents
      let hasSynthesisData = false;
      if (allDocumentsAnalyzed) {
        try {
          const synthesisResponse = await api.get(`/api/analysis/process-synthesis/${currentProject.id}`, {
            headers: { Authorization: `Bearer ${token}` }
          });
          const synthesis = synthesisResponse.data;
          hasSynthesisData = !!(synthesis && (synthesis.process_steps || synthesis.synthesis_text));
        } catch (synthError) {
          // If synthesis endpoint fails, assume no synthesis exists
          hasSynthesisData = false;
        }
      }
      setHasSynthesis(hasSynthesisData);

    } catch (error) {
      console.error('Error checking project prerequisites:', error);
      // Reset states on error
      setHasDocuments(false);
      setHasAnalysis(false);
      setHasSynthesis(false);
      setDocumentCount(0);
    } finally {
      setPrereqsLoaded(true);
    }
  }, [currentProject?.id]);

  const loadProcessMaps = useCallback(async () => {
    if (!currentProject) return;

    try {
      setLoading(true);
      const token = localStorage.getItem('jarvis_access_token');

      const timestamp = Date.now();

      // Load ALL process maps for this project
      const allMapsResponse = await api.get(
        `/api/process-maps/project/${currentProject.id}?_t=${timestamp}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            'Cache-Control': 'no-cache, no-store, must-revalidate',
            'Pragma': 'no-cache',
            'Expires': '0'
          },
          signal: pollAbortRef.current?.signal,
        }
      );

      const allMaps = allMapsResponse.data.process_maps || [];

      // Validate each map has proper structure
      const validatedMaps = allMaps.map((map: { map_data: { elements: { processes: any; }; }; id: any; }) => {
        if (!map.map_data) {
          console.warn(`Map ${map.id} has no map_data`);
          return map;
        }

        if (!map.map_data.elements) {
          console.warn(`Map ${map.id} has map_data but no elements structure - data may be corrupted`);
        }

        return map;
      });

      setAllProcessMaps(validatedMaps);

      // Set current map based on selected level
      const currentLevelMap = validatedMaps.find((map: { level: number; }) => map.level === selectedLevel);

      if (currentLevelMap) {
        setCurrentMap(currentLevelMap);
      } else if (autoLevelPickedForProject.current !== currentProject.id) {
        // Same landing rule as forceRefreshProcessData, which is the only path
        // that had it — and it runs only when an update is already pending. On
        // an ordinary page load the tab stayed on its L1 default and the screen
        // read "No Process Map for Level 1" with an L3 map one tab over.
        const levelToShow = resolveLevelToShow(validatedMaps, selectedLevel, readLastViewedLevel(currentProject.id));
        const best = levelToShow === null
          ? null
          : validatedMaps.find((map: { level: number; }) => map.level === levelToShow);

        if (best) {
          autoLevelPickedForProject.current = currentProject.id;
          selectLevel(best.level);
          setCurrentMap(best);
        } else {
          setCurrentMap(null);
        }
      } else {
        setCurrentMap(null);
      }

    } catch (error: any) {
      if (wasAborted(error)) return;
      if (error.response?.status !== 404) {
        showApiError(error, 'Failed to load process maps');
      }
      // Reset states on error
      setAllProcessMaps([]);
      setCurrentMap(null);
    } finally {
      setLoading(false);
    }
  }, [currentProject, selectedLevel, selectLevel]);

  useEffect(() => {
    if (currentProject?.id) {
      setPrereqsLoaded(false);
      checkProjectPrerequisites();
      loadProcessMaps();
      loadAnalysisResults();
      checkAuditEnhancementStatus();
    } else {
      // No project selected (direct navigation to /app/process-maps before a
      // project is chosen, or context still hydrating). Resolve the prerequisite
      // gate so the view shows the "Please select a project" banner instead of a
      // spinner that never ends. When the project arrives this effect re-runs.
      checkProjectPrerequisites();
    }
  }, [currentProject?.id, checkAuditEnhancementStatus, checkProjectPrerequisites]);

  // ✅ Reload process maps when mapRefreshCounter changes (triggered by validation updates)
  useEffect(() => {
    if (currentProject?.id && mapRefreshCounter > 0) {
      loadProcessMaps();
    }
  }, [mapRefreshCounter, currentProject?.id, loadProcessMaps]);

  // ✅ NEW: Listen for auto-sync events from Stakeholder Tab
  useEffect(() => {
    const handleProcessMapsUpdated = (event: CustomEvent) => {
      const { projectId } = event.detail;

      // Only reload if it's for the current project
      if (projectId === currentProject?.id) {
        console.log('🔄 Auto-sync completed, reloading process maps...');

        // Reload process maps to show updated data
        loadProcessMaps();

        // Show a subtle notification
        toast.success('Process maps updated!', {
          duration: 3000,
          icon: '🔄'
        });
      }
    };

    window.addEventListener('process-maps-updated' as any, handleProcessMapsUpdated);

    return () => {
      window.removeEventListener('process-maps-updated' as any, handleProcessMapsUpdated);
    };
  }, [currentProject?.id, loadProcessMaps]);

  // ✅ REMOVED: No longer need state restoration since we're not doing page reload
  // React Query handles cache invalidation and refetch automatically

  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible' && currentProject?.id) {
        checkProjectPrerequisites();
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [currentProject?.id, checkProjectPrerequisites])

  const processLevels = [
    { value: 1, name: 'Level 1: Strategic', description: 'High-level business objectives and outcomes' },
    { value: 2, name: 'Level 2: Tactical', description: 'Core business processes and cross-functional workflows' },
    { value: 3, name: 'Level 3: Operational', description: 'Detailed process steps and procedures' },
    { value: 4, name: 'Level 4: Execution', description: 'Task-level activities and system interactions' }
  ];

  const loadAnalysisProgress = useCallback(async () => {
    if (!currentProject) return;
    try {
      const response = await api.get(`/api/analysis/progress/${currentProject.id}`, {
        signal: pollAbortRef.current?.signal,
      });
      setAnalysisProgress(response.data);
    } catch (error) {
      if (wasAborted(error)) return;
      console.error('Failed to load analysis progress');
    }
  }, [currentProject]);

  const loadAnalysisResults = useCallback(async () => {
    if (!currentProject) return;
    try {
      // FIXED: Remove cache headers, use URL timestamp instead
      const timestamp = Date.now();
      const response = await api.get(`/api/analysis/results/${currentProject.id}?_t=${timestamp}`, {
        signal: pollAbortRef.current?.signal,
      });

      setAnalysisResults(response.data);

    } catch (error) {
      if (wasAborted(error)) return;
      console.error('Failed to load analysis results:', error);
    }
  }, [currentProject]);


  useEffect(() => {
    if (currentProject) {
      loadAnalysisProgress();
      loadAnalysisResults();
    }
  }, [currentProject, loadAnalysisProgress, loadAnalysisResults]);

  useEffect(() => {
    let resultsInterval: NodeJS.Timeout | null = null;
    if (isAnalyzing && currentProject) {
      resultsInterval = setInterval(() => {
        loadAnalysisResults();
      }, 3000);
    }

    return () => {
      if (resultsInterval) {
        clearInterval(resultsInterval);
        resultsInterval = null;
      }
    };
  }, [isAnalyzing, loadAnalysisResults, currentProject]);

  // Poll analysis PROGRESS while analyzing. This is the fix for the
  // "zombie '1 of 2 analyzing'" bug — loadAnalysisProgress() used to be
  // called only once when a project loaded, so once the frontend had
  // its first snapshot it never re-fetched. Even after the backend
  // finished all documents and updated their analysis_status, the
  // frontend kept showing stale "1 of 2 analyzing". The completion
  // detection below (next useEffect) depends on analysisProgress
  // changing over time, so without this interval the UI never
  // transitioned to a completed state. Pattern mirrors Analysis.tsx
  // and the results-polling loop above.
  useEffect(() => {
    let progressInterval: NodeJS.Timeout | null = null;
    if (isAnalyzing && currentProject) {
      progressInterval = setInterval(() => {
        loadAnalysisProgress();
      }, 2000);
    }
    return () => {
      if (progressInterval) {
        clearInterval(progressInterval);
        progressInterval = null;
      }
    };
  }, [isAnalyzing, loadAnalysisProgress, currentProject]);

  // Timeout guard — even with progress polling, a silent worker crash
  // or a lost queue job can leave documents permanently in "analyzing"
  // state on the backend. In that case the frontend would still hang
  // because (completed + failed) never reaches total. We track when
  // analysis started and, if it runs past ANALYSIS_TIMEOUT_MS without
  // completing, flag it as timed out and exit the analyzing UI so the
  // user can retry. 10 minutes is generous — typical 2-document analysis
  // completes in well under 3 minutes — but gives buffer for slow LLM
  // responses without being so long users give up first.
  useEffect(() => {
    if (isAnalyzing) {
      setAnalysisStartTime((prev) => prev ?? Date.now());
      setAnalysisTimedOut(false);
    } else {
      setAnalysisStartTime(null);
    }
  }, [isAnalyzing]);

  useEffect(() => {
    if (!isAnalyzing || analysisStartTime === null) return;
    const ANALYSIS_TIMEOUT_MS = 10 * 60 * 1000;
    const timeoutCheck = setInterval(() => {
      if (Date.now() - analysisStartTime > ANALYSIS_TIMEOUT_MS) {
        console.warn(
          '[ProcessMaps] Document analysis exceeded expected duration — exiting analyzing UI',
        );
        setAnalysisTimedOut(true);
        setIsAnalyzing(false);
        clearInterval(timeoutCheck);
      }
    }, 15000);
    return () => clearInterval(timeoutCheck);
  }, [isAnalyzing, analysisStartTime]);

  // Surface timeout to the user. We deliberately show the toast in an
  // effect (not inline in the timeout check) so React state updates land
  // cleanly first — firing toast inside the setInterval callback would
  // race with the isAnalyzing transition and occasionally flash an
  // "analyzing" spinner behind the error toast. The toast also re-fetches
  // progress one last time so the final counts in the UI reflect whatever
  // did complete before we gave up.
  useEffect(() => {
    if (!analysisTimedOut) return;
    toast.error(
      'Document analysis is taking longer than expected. The backend may be stuck — please retry.',
      { duration: 6000 },
    );
    loadAnalysisProgress();
  }, [analysisTimedOut, loadAnalysisProgress]);

  useEffect(() => {
    if (isAnalyzing && analysisProgress) {
      const { completed, failed, total } = analysisProgress;
      if ((completed + failed === total) && total > 0) {
        setIsAnalyzing(false);
        loadAnalysisResults();
      }
    }
  }, [isAnalyzing, analysisProgress, loadAnalysisResults]);

  useEffect(() => {
    // Only run post-analysis polling if we're NOT analyzing AND we don't have complete synthesis data
    if (isAnalyzing || !currentProject) {
      return;
    }

    // Check if we have complete analysis results
    const hasSynthesis = analysisResults?.process_synthesis;
    const hasAnalysisComplete = analysisResults && hasSynthesis;

    // If we have complete results, don't poll
    if (hasAnalysisComplete) {
      return;
    }

    let attempts = 0;
    const maxAttempts = 20; // Reduced from 40 to prevent excessive polling
    let intervalId: NodeJS.Timeout | null = null;

    intervalId = setInterval(() => {
      if (attempts >= maxAttempts) {
        console.warn("Post-analysis polling timed out after 20 attempts.");
        if (intervalId) clearInterval(intervalId);
        return;
      }

      if (currentProject) {
        loadAnalysisResults();
        attempts++;
      } else {
        if (intervalId) clearInterval(intervalId);
      }
    }, 5000); // Increased from 3000 to 5000 to reduce frequency

    return () => {
      if (intervalId) {
        clearInterval(intervalId);
        intervalId = null;
      }
    };
  }, [isAnalyzing, analysisResults?.process_synthesis, analysisResults?.sop, loadAnalysisResults, currentProject]);


  const stopMapGeneration = async () => {
    if (!currentProject || cancellingGeneration) return;
    mapCancelRequestedRef.current = true;
    setCancellingGeneration(true);
    try {
      await api.post(`/api/analysis/cancel-process-map/${currentProject.id}`, { level: selectedLevel });
      setShowLoader(false);
      setGenerating(false);
      toast.success('Process map generation stopped. The current AI call may finish, then no further calls run.');
    } catch (error: any) {
      toast.error(getApiErrorMessage(error, 'Could not stop generation'));
    } finally {
      setCancellingGeneration(false);
    }
  };

  const generateMapForLevel = async (level: number) => {
    if (!currentProject || generating) return;

    mapCancelRequestedRef.current = false;
    setCancellingGeneration(false);
    setGenerating(true);
    selectLevel(level);
    setShowLoader(true);
    setLoaderOperation(allProcessMaps.find(map => map.level === level) ? 'regenerating' : 'generating');
    setLoaderProgress(0);
    setLoaderStage('Starting process map generation...');

    try {
      let token = tokenStorage.getAccessToken();

      const existingMap = allProcessMaps.find(map => map.level === level);
      const requestConfig: any = { timeout: 30000 };
      if (token) {
        requestConfig.headers = { Authorization: `Bearer ${token}` };
      }

      // ✅ FIX: Only send force_regenerate=true when REGENERATING (existing map exists)
      // This allows preserve mode to work on first generation
      const isRegenerating = existingMap ? true : false;

      const startResponse = await api.post(
        `/api/analysis/generate-process-map-async/${currentProject.id}`,
        {
          level: level,
          force_regenerate: isRegenerating,  // ✅ Only true when regenerating
          delete_existing: isRegenerating    // ✅ Same condition
        },
        requestConfig
      );

      if (startResponse.data.status !== 'in_progress') {
        throw new Error('Failed to start generation process');
      }

      const generationId = startResponse.data.generation_id;

      setLoaderProgress(10);
      setLoaderStage('Generation started successfully...');

      // Polling logic (unchanged from your working version)
      let attempts = 0;
      const maxAttempts = level === 4 ? 200 : 120;
      let isCompleted = false;

      const pollStatus = async (): Promise<boolean> => {
        // Wrap ONLY the network call in try-catch so that a backend 'failed'
        // status is not accidentally swallowed as a transient network error.
        let status: any;
        try {
          const statusResponse = await api.get(`/api/analysis/generate-process-map-status/${generationId}`, {
            timeout: 300000
          });
          status = statusResponse.data;
        } catch (networkError: any) {
          console.warn(`Status check ${attempts + 1} failed:`, networkError.message);
          if (attempts > 60) {
            throw new Error('Lost connection to generation process');
          }
          return false;
        }

        if (status.progress >= 0) setLoaderProgress(status.progress);
        if (status.message) setLoaderStage(status.message);

        if (status.status === 'completed') {
          setLoaderProgress(95);
          setLoaderStage('Process map completed! Loading results...');
          return true;
        }

        if (status.status === 'cancelled') {
          throw new Error('GENERATION_CANCELLED');
        }

        if (status.status === 'failed') {
          // Throw outside the try-catch so it propagates to the outer handler
          // which calls setShowLoader(false). Previously this was thrown inside
          // the try block and swallowed by the catch, leaving the modal frozen.
          throw new Error(status.error || 'Generation failed');
        }

        return false;
      };

      while (attempts < maxAttempts && !isCompleted) {
        if (mapCancelRequestedRef.current) return;
        await new Promise(resolve => setTimeout(resolve, 10000));
        if (mapCancelRequestedRef.current) return;
        attempts++;

        try {
          isCompleted = await pollStatus();
        } catch (pollError) {
          console.error('Polling failed:', pollError);
          throw pollError;
        }
      }

      if (!isCompleted) {
        throw new Error('Generation timed out. The process may still be running in the background.');
      }

      // Success handling (unchanged)
      setLoaderStage('Loading completed process map...');
      await new Promise(resolve => setTimeout(resolve, 2000));

      await forceRefreshProcessData();

      setLoaderProgress(100);
      setLoaderStage('Complete!');

      setTimeout(() => {
        setShowLoader(false);

        const operationText = loaderOperation === 'regenerating' ? 'regenerated' : 'generated';
        toast.success(`Level ${level} process map ${operationText} successfully!`, {
          duration: 4000,
          icon: '🎯',
        });

        // ✅ NEW: Start checking audit enhancement status for Level 3
        if (level === 3) {
          // Show enhancement status message
          setTimeout(() => {
            toast('🔄 Background: Generating detailed audit info for process steps...', {
              duration: 6000,
              icon: '⚙️',
            });
          }, 2000);

          // Start polling for audit enhancement status
          const pollInterval = setInterval(async () => {
            console.log('🔄 Polling for audit enhancement status...');
            try {
              const token = localStorage.getItem('jarvis_access_token');
              const response = await api.get(`/api/analysis/audit-enhancement-status/${currentProject.id}`, {
                headers: { Authorization: `Bearer ${token}` }
              });

              const data = response.data;
              const newStatus = {
                status: data.enhancement_status,
                progress: data.enhancement_progress || 0,
                enhanced_steps_count: data.enhanced_steps_count || 0,
                total_steps: data.total_steps || 0,
                error: data.enhancement_error
              };

              console.log(`🔍 Current audit status: ${newStatus.status} (${newStatus.progress}%)`);
              setAuditEnhancementStatus(newStatus);

              // Stop polling if completed, failed, or not applicable (preserve mode)
              if (newStatus.status === 'completed') {
                console.log('✅ Audit enhancement completed! Stopping polling.');
                clearInterval(pollInterval);

                // Reload process maps to show updated audit data
                console.log('🔄 Reloading process maps with audit data...');
                await loadProcessMaps();

                // Hide the loading spinner
                setShowLoader(false);
                setGenerating(false);

                toast.success('✅ Audit info generation completed!', {
                  duration: 4000,
                  icon: '📋',
                });
              } else if (newStatus.status === 'failed') {
                console.log('❌ Audit enhancement failed! Stopping polling.');
                clearInterval(pollInterval);
                toast.error('❌ Audit info generation failed', {
                  duration: 4000,
                });
              } else if (newStatus.status === 'not_applicable') {
                console.log('🔒 Preserve mode detected - audit enhancement not applicable. Stopping polling.');
                clearInterval(pollInterval);

                // Reload process maps to show the preserved map
                console.log('🔄 Reloading process maps to display preserved document...');
                await loadProcessMaps();

                // Hide the loading spinner
                setShowLoader(false);
                setGenerating(false);

                toast.success('🔒 Process map preserved in original format', {
                  duration: 4000,
                  icon: '✅',
                });
              }
            } catch (error) {
              console.error('❌ Error polling audit status:', error);
            }
          }, 3000);

          // Stop polling after 5 minutes to prevent infinite polling
          setTimeout(() => {
            clearInterval(pollInterval);
          }, 300000);
        }

      }, 1000);

    } catch (error: any) {
      console.error(`Level ${level} process map generation failed:`, error);

      setShowLoader(false);

      if (mapCancelRequestedRef.current || error?.message === 'GENERATION_CANCELLED') {
        return;
      }

      const errorMessage = getApiErrorMessage(error, 'Generation failed');

      // Simple error handling without auth redirects
      if (errorMessage.includes('timed out')) {
        toast.error(
          `Generation is taking longer than expected. Please check back in a few minutes or refresh the page.`,
          { duration: 8000 }
        );
      } else if (errorMessage.includes('already in progress')) {
        toast.error(
          'Process map generation is already running. Please wait for it to complete.',
          { duration: 5000 }
        );
      } else if (error.response?.status === 401) {
        toast.error('Authentication issue. Please refresh the page and try again.', { duration: 5000 });
      } else {
        toast.error(`Failed to generate Level ${level} process map: ${errorMessage}`, {
          duration: 6000
        });
      }

    } finally {
      setGenerating(false);
    }
  };

  const verifyAuthentication = async (): Promise<string | null> => {
    // Try multiple token storage locations
    const tokenSources = [
      () => localStorage.getItem('jarvis_access_token'),
      () => sessionStorage.getItem('jarvis_access_token')
    ];

    for (const getToken of tokenSources) {
      const token = getToken();
      if (token) {
        return token;
      }
    }

    return null;
  };

  // You can also add this to your component to check authentication on mount
  useEffect(() => {
    const checkAuth = async () => {
      const token = await verifyAuthentication();
      if (!token) {
        console.warn('No valid authentication found');
        // Handle authentication failure
      }
    };

    if (currentProject) {
      checkAuth();
    }
  }, [currentProject]);


  const generateMap = () => generateMapForLevel(selectedLevel);

  const handleLevelChange = async (newLevel: number) => {
    if (hasUnsavedChanges) {
      setConfirmRequest({
        title: 'Discard unsaved changes?',
        message: `You have unsaved edits to this map. Switching to ${processLevels.find(l => l.value === newLevel)?.name} discards them.`,
        confirmLabel: 'Discard and switch',
        cancelLabel: 'Stay here',
        tone: 'danger',
        onConfirm: () => { void applyLevelChange(newLevel); },
      });
      return;
    }

    await applyLevelChange(newLevel);
  };

  const applyLevelChange = async (newLevel: number) => {
    // Only show loader for level switching if there's an existing map to load
    const existingMap = allProcessMaps.find(map => map.level === newLevel);

    if (existingMap) {
      setShowLoader(true);
      setLoaderOperation('switching');
      setLoaderProgress(30);
      setLoaderStage(`Loading ${processLevels.find(l => l.value === newLevel)?.name}...`);
    }

    // A deliberate switch counts as the level having been chosen, so the
    // auto-pick in loadProcessMaps never pulls the user off an empty level they
    // opened on purpose to generate it.
    if (currentProject?.id) {
      autoLevelPickedForProject.current = currentProject.id;
    }

    // Update states
    selectLevel(newLevel);
    setIsEditMode(false);
    setHasUnsavedChanges(false);
    setEditedMapData(null);
    setPendingDeleteIds([]);

    // Clear cached data
    setEnhancedProcesses([]);

    if (existingMap) {
      // Simulate loading for better UX
      setTimeout(() => setLoaderProgress(70), 200);
      setTimeout(() => setLoaderProgress(100), 400);

      setTimeout(() => {
        setCurrentMap(existingMap);
        setShowLoader(false);
        toast.success(`Loaded ${processLevels.find(l => l.value === newLevel)?.name} process map`);

        // ✅ NEW: Check audit enhancement status when switching to Level 3
        if (newLevel === 3) {
          checkAuditEnhancementStatus();
        }
      }, 600);
    } else {
      setCurrentMap(null);

      if (hasAllPrerequisites) {
        const levelInfo = processLevels.find(l => l.value === newLevel);
        toast(`No process map found for ${levelInfo?.name}. Click "Generate" to create one.`, {
          icon: 'ℹ️',
          duration: 4000,
        });
      }
    }
  };


  const handleZoomChange = (newZoom: number) => {
    setZoomLevel(Math.max(25, Math.min(200, newZoom)));
  };

  const handleProcessReorder = (reorderedProcesses: any[]) => {
    if (!currentMap || !currentMap.map_data) return;

    setEditedMapData((prev: any) => {
      const base = prev || currentMap.map_data;
      return {
        ...base,
        elements: {
          ...base.elements,
          processes: reorderedProcesses,
        },
      };
    });
    setHasUnsavedChanges(true);
  };

  const handleDecisionEdit = (index: number, fields: { isDecision: boolean; decisionPaths?: any }) => {
    setEditedMapData((prev: any) => applyManualDecisionEdit(prev || currentMap?.map_data, index, fields));
    setHasUnsavedChanges(true);
  };

  const handleFindingAction = (
    finding: any,
    action: 'approve' | 'adjust' | 'reject' | 'keep_flagged',
    overrides?: any,
  ) => {
    const base = editedMapData || currentMap?.map_data;
    const result = applyAccuracyGateFinding(base, finding, {
      action,
      overrides,
      pendingDeleteIds,
      sourceHasDiamonds: sourceHasDiamonds(currentMap?.generation_metadata?.accuracy_gate),
    });
    if (result.error && action !== 'keep_flagged') {
      toast.error(result.error);
      if (result.status === finding.status) return;
    }
    if (action === 'approve' || action === 'adjust') {
      setEditedMapData(result.mapData);
      setHasUnsavedChanges(true);
    }
    setPendingDeleteIds(result.pendingDeleteIds);
    setFindingSessionStatus((prev) => ({ ...prev, [finding.id]: result.status }));
  };

  const handleStepDelete = async (stepId: string) => {
    if (!currentProject?.id || !currentMap?.id) {
      toast.error('No project or process map selected');
      return;
    }

    try {
      const token = localStorage.getItem('jarvis_access_token');
      const response = await api.delete(`/api/process-maps/${currentMap.id}/steps/${stepId}`, {
        headers: { Authorization: `Bearer ${token}` },
        data: { project_id: currentProject.id }
      });

      if (response.data.success) {
        toast.success(`Step deleted successfully`);
        setIsEditMode(false);
        setHasUnsavedChanges(false);
        setEditedMapData(null);
        await loadProcessMaps();
        setMapRefreshCounter(prev => prev + 1);
      }
    } catch (error: any) {
      console.error('Failed to delete step:', error);
      showApiError(error, 'Failed to delete step');
    }
  };

  const handlePendingDeleteChange = React.useCallback((ids: string[]) => {
    setPendingDeleteIds(ids);
    setHasUnsavedChanges(prev => prev || ids.length > 0);
  }, []);

  const handleSaveChanges = async () => {
    if (!currentMap || !editedMapData || !currentProject) {
      toast.error('No changes to save');
      return;
    }

    setSavingChanges(true);
    try {
      // ✅ COMPREHENSIVE FIX: Clean all L3_ prefixes and filter out track steps before saving
      const cleanMapData = {
        ...editedMapData,
        elements: {
          ...editedMapData.elements,
          processes: editedMapData.elements.processes
            // Drop steps the user marked for deletion in edit mode.
            .filter((step: any) => !pendingDeleteIds.includes(String(step.id)))
            // ⚠️ CRITICAL: Filter out track steps (frontend-only visualization artifacts)
            .filter((step: any) => {
              // Remove steps with track IDs (e.g., "activity_1_TrackA", "activity_1_TrackB")
              if (step.id && typeof step.id === 'string') {
                if (step.id.endsWith('_TrackA') || step.id.endsWith('_TrackB')) {
                  return false;
                }
              }
              // Remove steps explicitly marked as track steps
              if (step.isTrackStep === true) {
                return false;
              }
              return true;
            })
            .map((step: any) => {
              const cleanedStep = { ...step };

              // Helper function to strip L3_ prefix and track suffixes from any step ID
              const cleanStepId = (id: any): any => {
                if (!id) return id;
                if (typeof id === 'string') {
                  // Strip L3_ prefix and _TrackA/_TrackB suffixes
                  return id.replace(/^L3_/, '').replace(/_(TrackA|TrackB)$/, '');
                }
                return id;
              };

              // Clean step ID
              cleanedStep.id = cleanStepId(cleanedStep.id);

              // Clean connections array
              if (cleanedStep.connections && Array.isArray(cleanedStep.connections)) {
                cleanedStep.connections = cleanedStep.connections
                  .map(cleanStepId)
                  // Filter out connections to track steps that no longer exist
                  .filter((conn: string) => conn && !conn.endsWith('_TrackA') && !conn.endsWith('_TrackB'));
              }

              // Clean decisionPaths (camelCase - frontend format)
              if (cleanedStep.decisionPaths) {
                if (cleanedStep.decisionPaths.yes) {
                  cleanedStep.decisionPaths.yes = cleanStepId(cleanedStep.decisionPaths.yes);
                }
                if (cleanedStep.decisionPaths.no) {
                  cleanedStep.decisionPaths.no = cleanStepId(cleanedStep.decisionPaths.no);
                }
                if (cleanedStep.decisionPaths.completion) {
                  cleanedStep.decisionPaths.completion = cleanStepId(cleanedStep.decisionPaths.completion);
                }
                if (cleanedStep.decisionPaths.parallel_tracks) {
                  const tracks = cleanedStep.decisionPaths.parallel_tracks;

                  // Clean track A
                  if (tracks.track_A) {
                    if (tracks.track_A.next_steps && Array.isArray(tracks.track_A.next_steps)) {
                      tracks.track_A.next_steps = tracks.track_A.next_steps.map(cleanStepId);
                    }
                    if (tracks.track_A.convergence) {
                      tracks.track_A.convergence = cleanStepId(tracks.track_A.convergence);
                    }
                  }

                  // Clean track B
                  if (tracks.track_B) {
                    if (tracks.track_B.next_steps && Array.isArray(tracks.track_B.next_steps)) {
                      tracks.track_B.next_steps = tracks.track_B.next_steps.map(cleanStepId);
                    }
                    if (tracks.track_B.convergence) {
                      tracks.track_B.convergence = cleanStepId(tracks.track_B.convergence);
                    }
                  }

                  // Clean global convergence
                  if (tracks.convergence) {
                    tracks.convergence = cleanStepId(tracks.convergence);
                  }
                }
              }

              // Clean decision_paths (snake_case - database format)
              if (cleanedStep.decision_paths) {
                if (cleanedStep.decision_paths.yes) {
                  cleanedStep.decision_paths.yes = cleanStepId(cleanedStep.decision_paths.yes);
                }
                if (cleanedStep.decision_paths.no) {
                  cleanedStep.decision_paths.no = cleanStepId(cleanedStep.decision_paths.no);
                }
                if (cleanedStep.decision_paths.completion) {
                  cleanedStep.decision_paths.completion = cleanStepId(cleanedStep.decision_paths.completion);
                }
                if (cleanedStep.decision_paths.parallel_tracks) {
                  const tracks = cleanedStep.decision_paths.parallel_tracks;

                  // Clean track A
                  if (tracks.track_A) {
                    if (tracks.track_A.next_steps && Array.isArray(tracks.track_A.next_steps)) {
                      tracks.track_A.next_steps = tracks.track_A.next_steps.map(cleanStepId);
                    }
                    if (tracks.track_A.convergence) {
                      tracks.track_A.convergence = cleanStepId(tracks.track_A.convergence);
                    }
                  }

                  // Clean track B
                  if (tracks.track_B) {
                    if (tracks.track_B.next_steps && Array.isArray(tracks.track_B.next_steps)) {
                      tracks.track_B.next_steps = tracks.track_B.next_steps.map(cleanStepId);
                    }
                    if (tracks.track_B.convergence) {
                      tracks.track_B.convergence = cleanStepId(tracks.track_B.convergence);
                    }
                  }

                  // Clean global convergence
                  if (tracks.convergence) {
                    tracks.convergence = cleanStepId(tracks.convergence);
                  }
                }
              }

              // Remove frontend-only fields that should never be saved to database
              delete cleanedStep.isTrackStep;
              delete cleanedStep.trackType;
              delete cleanedStep.originalStepId;

              return cleanedStep;
            })
        }
      };

      const token = localStorage.getItem('jarvis_access_token');
      const response = await api.put(`/api/process-maps/${currentMap.id}/reorder-steps`, {
        map_data: cleanMapData,
        project_id: currentProject.id
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });

      setIsEditMode(false);
      setEditedMapData(null);
      setHasUnsavedChanges(false);
      setPendingDeleteIds([]);
      setFindingSessionStatus({});

      // ✅ FIX: Use React Query to invalidate and refetch instead of page reload
      await queryClient.invalidateQueries({
        queryKey: ['processMaps', currentProject.id]
      });

      await loadProcessMaps();
      onApprovalStateChanged?.();

      toast.success('Changes saved successfully!');

      try {
        toast.loading('Re-running accuracy gate…', { id: 'post-edit-gate', duration: Infinity });
        const verifyRes = await api.post(`/api/analysis/process-maps/${currentMap.id}/verify`, { mode: 'standard' });
        const gate = verifyRes.data?.accuracy_gate;
        await loadProcessMaps();
        toast.success(gate ? accuracyGateHeadline(gate) : 'Accuracy gate re-ran', { id: 'post-edit-gate', duration: 4000 });
      } catch (gateErr) {
        console.error('Post-edit accuracy gate failed:', gateErr);
        toast.error('Saved, but the accuracy gate could not re-run. Use Run Verifier.', { id: 'post-edit-gate', duration: 5000 });
      }

      // ✅ REVALIDATION: Prompt user to validate reordered steps
      setTimeout(() => {
        toast((t) => (
          <div className="bg-white rounded-lg shadow-xl border border-gray-200 p-4 max-w-md">
            <div className="flex items-start space-x-3">
              {/* Icon */}
              <div className="flex-shrink-0">
                <div className="w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center">
                  <svg className="w-6 h-6 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                </div>
              </div>

              {/* Content */}
              <div className="flex-1 min-w-0">
                <h3 className="text-sm font-semibold text-gray-900 mb-1">
                  Steps have been reordered
                </h3>
                <p className="text-sm text-gray-600 mb-3">
                  Would you like to validate the new sequence?
                </p>

                {/* Buttons */}
                <div className="flex items-center space-x-2">
                  <button
                    onClick={() => {
                      toast.dismiss(t.id);
                      startValidationFromButton();
                    }}
                    className="px-4 py-2 bg-blue-600 text-white text-sm font-medium rounded-md hover:bg-blue-700 transition-colors shadow-sm"
                  >
                    Start Validation
                  </button>
                  <button
                    onClick={() => toast.dismiss(t.id)}
                    className="px-4 py-2 bg-white text-gray-700 text-sm font-medium rounded-md border border-gray-300 hover:bg-gray-50 transition-colors"
                  >
                    Later
                  </button>
                </div>
              </div>

              {/* Close button */}
              <button
                onClick={() => toast.dismiss(t.id)}
                className="flex-shrink-0 text-gray-400 hover:text-gray-600 transition-colors"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
          </div>
        ), {
          duration: 10000,
          position: 'top-center',
          style: {
            background: 'transparent',
            boxShadow: 'none',
            padding: 0,
          }
        });
      }, 1000);
    } catch (error: any) {
      console.error('Failed to save changes:', error);
      showApiError(error, 'Failed to save changes');
    } finally {
      setSavingChanges(false);
    }
  };

  const handleCancelEdit = () => {
    const discard = () => {
      setIsEditMode(false);
      setEditedMapData(null);
      setHasUnsavedChanges(false);
      setPendingDeleteIds([]);
      setFindingSessionStatus({});
    };

    if (hasUnsavedChanges || pendingDeleteIds.length > 0) {
      const pendingDeletes = pendingDeleteIds.length
        ? ` ${pendingDeleteIds.length} step${pendingDeleteIds.length > 1 ? 's are' : ' is'} marked for deletion.`
        : '';
      setConfirmRequest({
        title: 'Discard your edits?',
        message: `Leaving edit mode discards every change you have not saved.${pendingDeletes} Use Save instead if you want to keep them.`,
        confirmLabel: 'Discard edits',
        cancelLabel: 'Keep editing',
        tone: 'danger',
        onConfirm: discard,
      });
      return;
    }

    discard();
  };

  const handleToggleEditMode = () => {
    if (isEditMode) {
      handleCancelEdit();
    } else {
      setIsEditMode(true);
      setEditedMapData(currentMap?.map_data || null);
      setFindingSessionStatus({});
    }
  };

  const getProcessData = () => {

    if (currentMap && currentMap.map_data) {
      const mapData = isEditMode && editedMapData ? editedMapData : currentMap.map_data;

      if (!mapData || !mapData.elements) {
        console.warn('⚠️ Map data exists but has no elements structure');
        // Try to extract from root level
        if (mapData.processes) {
          mapData.elements = { processes: mapData.processes };
        } else {
          return { processes: [], elements: {}, swimlanes: [] };
        }
      }

      const elements = mapData.elements || {};
      let processes = [];

      if (elements.processes && Array.isArray(elements.processes)) {
        processes = elements.processes.map((process: any, index: number) => {
      
          const stepNumber = String(process.step_number || '');
          const isTrackSuffixed =
            (typeof process.id === 'string' && /_Track[AB]$/i.test(process.id)) ||
            /[AB]$/i.test(stepNumber);

          return {
            ...process,
            id: process.id || `process_${index}`,
            name: process.name || process.description || `Process ${index + 1}`,
            description: process.detailed_description || process.description || process.name,
            assignee: process.assignee || process.actor || 'Unassigned',
            swimlane: process.assignee || process.actor || 'Unassigned',
            actor: process.actor || process.assignee,
            category: process.action_type,
            type: process.action_type,
            level: process.level || selectedLevel,
            is_decision: process.is_decision || process.action_type === 'decision' || process.bpmn_type === 'gateway',
            step_index: index,
            connections: !isTrackSuffixed && index < elements.processes.length - 1 ? [`${process.id || `process_${index + 1}`}`] : [],
            sync_source: 'process_map_fresh',
            map_id: currentMap.id,
            updated_at: currentMap.updated_at
          };
        });
      }

      const swimlanes = elements.swimlanes || [
        { id: 'lane_1', name: 'Business Owner', activities: [], description: 'Primary business owner responsibilities' },
        { id: 'lane_2', name: 'Process Owner', activities: [], description: 'Process management and oversight' },
        { id: 'lane_3', name: 'Team Members', activities: [], description: 'Execution and delivery' },
        { id: 'lane_4', name: 'External Partners', activities: [], description: 'Third-party interactions' },
        { id: 'lane_5', name: 'Systems', activities: [], description: 'Automated processes and tools' }
      ];

      return { processes, elements, swimlanes };
    }

    return { processes: [], elements: {}, swimlanes: [] };
  };


  const forceRefreshProcessData = useCallback(async () => {
    setEnhancedProcesses([]);
    setCurrentMap(null);

    try {
      const timestamp = Date.now();

      const [mapsResult] = await Promise.all([
        api.get(`/api/process-maps/project/${currentProject!.id}?_t=${timestamp}`, {
          headers: {
            Authorization: `Bearer ${localStorage.getItem('jarvis_access_token')}`,
          },
          signal: pollAbortRef.current?.signal,
        }),
        loadAnalysisResults(),
        checkProjectPrerequisites()
      ]);

      const allMaps = mapsResult.data.process_maps || [];
      setAllProcessMaps(allMaps);

      const currentLevelMap = allMaps.find((map: { level: number; }) => map.level === selectedLevel);
      if (currentLevelMap) {
        setCurrentMap(currentLevelMap);
      } else if (autoLevelPickedForProject.current !== currentProject!.id) {
        // The tab defaults to L1, which is empty on most projects — the screen
        // opened on "No Process Map for Level 1" while an L3 map sat one tab
        // over. Land on a level that actually has a map. Once per project, so a
        // deliberate switch to an empty level is not undone.
        const levelToShow = resolveLevelToShow(allMaps, selectedLevel, readLastViewedLevel(currentProject!.id));
        const best = levelToShow === null
          ? null
          : allMaps.find((map: { level: number; }) => map.level === levelToShow);

        if (best) {
          autoLevelPickedForProject.current = currentProject!.id;
          selectLevel(best.level);
          setCurrentMap(best);
        }
      }

    } catch (error) {
      // Several callers invoke this with a bare .then(), so a rethrow here
      // during navigation would surface as an unhandled rejection.
      if (wasAborted(error)) return;
      throw error;
    }
  }, [currentProject, selectedLevel, selectLevel, loadAnalysisResults, checkProjectPrerequisites]);

  // Handle pending updates after component mount
  useEffect(() => {
    if (currentProject?.id) {
      const pendingUpdates = procesMapUpdateManager.getPendingUpdates();
      if (pendingUpdates.length > 0) {
        setTimeout(() => {
          forceRefreshProcessData().then(() => {
            procesMapUpdateManager.clearAll();
          });
        }, 1000);
      }
    }
  }, [currentProject?.id, forceRefreshProcessData]);


  // Listen for process map updates
  useEffect(() => {
    const handleDirectProcessMapUpdate = (event: CustomEvent) => {
      const { projectId: eventProjectId, processMapId: eventProcessMapId } = event.detail;

      if (currentProject?.id === eventProjectId) {
        forceRefreshProcessData().then(() => {
          if (eventProcessMapId) {
            procesMapUpdateManager.markAsProcessed(eventProcessMapId);
          }
        });
      }
    };

    const handleGlobalProcessMapUpdate = (event: CustomEvent) => {
      const { processMapId } = event.detail;

      if (currentProject?.id && processMapId) {
        forceRefreshProcessData().then(() => {
          procesMapUpdateManager.markAsProcessed(processMapId);
        });
      }
    };

    window.addEventListener('processMapUpdated', handleDirectProcessMapUpdate as EventListener);
    window.addEventListener('globalProcessMapUpdateReceived', handleGlobalProcessMapUpdate as EventListener);

    return () => {
      window.removeEventListener('processMapUpdated', handleDirectProcessMapUpdate as EventListener);
      window.removeEventListener('globalProcessMapUpdateReceived', handleGlobalProcessMapUpdate as EventListener);
    };
  }, [currentProject?.id, forceRefreshProcessData]);


  const { processes: rawProcesses, swimlanes } = useMemo(() => getProcessData(), [currentMap, currentMap?.updated_at, selectedLevel, isEditMode, editedMapData, hasUnsavedChanges]);


  useEffect(() => {
    if (rawProcesses.length > 0 && currentProject) {
      const processesWithAssignees = rawProcesses.map((process: any) => ({
        ...process,
        assignee: process.assignee || 'Unassigned',
        sync_source: 'direct_preserve'
      }));

      setEnhancedProcesses(processesWithAssignees);
      setLoadingRoleAssignments(false);
      loadingRef.current = false;
    } else {
      setEnhancedProcesses([]);
      setLoadingRoleAssignments(false);
    }
  }, [rawProcesses.length, currentProject?.id, selectedLevel, currentMap?.id, currentMap?.updated_at]);

  const processes = isEditMode && editedMapData?.elements?.processes
    ? editedMapData.elements.processes
    : enhancedProcesses;


  useEffect(() => {
    // Auto-retry data loading if current map is missing but should exist
    if (currentProject && !currentMap && !loading && !generating) {
      const mapShouldExist = allProcessMaps.some(map => map.level === selectedLevel);
      if (mapShouldExist) {
        setTimeout(() => {
          loadProcessMaps();
        }, 1000);
      }
    }
  }, [currentProject, currentMap, loading, generating, allProcessMaps, selectedLevel, loadProcessMaps]);


  const renderProcessMapVisualization = () => {
    // Wait until both API calls have resolved before showing any prerequisite screens.
    // This prevents the "no docs → analysis needed → new doc detected" flash on every load.
    if (loading || !prereqsLoaded) {
      return (
        <div className="flex flex-col items-center justify-center py-20 gap-4">
          <div className="animate-spin rounded-full h-12 w-12 border-4 border-electric-blue border-t-transparent" />
          <p className="text-gray-400 text-sm">Loading process maps…</p>
        </div>
      );
    }

    // An already-existing process map is the ground truth that a project has
    // completed documents → analysis → synthesis → map generation before.
    // Re-deriving "has analysis" from the CURRENT document set (below) can
    // regress to false if a document was later added/replaced/reset — without
    // this check that would incorrectly show "start from scratch" prerequisite
    // screens over a project that already has a validated, approved map.
    const hasExistingMaps = allProcessMaps && allProcessMaps.length > 0;

    if (!hasDocuments && !hasExistingMaps) {
      return (
        <div className="text-center py-12 bg-gray-50 rounded-lg border-2 border-dashed border-gray-300">
          <div className="max-w-md mx-auto">
            <Upload className="w-16 h-16 text-gray-400 mx-auto mb-4" />
            <h3 className="text-xl font-medium text-gray-900 mb-2">No Documents Uploaded</h3>
            <p className="text-gray-500 mb-6">
              Process maps are generated from your uploaded documents. Please upload documents first to continue.
            </p>
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-4">
              <div className="flex items-start space-x-3">
                <AlertCircle className="w-5 h-5 text-blue-500 mt-0.5" />
                <div className="text-left">
                  <p className="text-sm font-medium text-blue-800 mb-1">Getting Started:</p>
                  <ol className="text-sm text-blue-700 space-y-1">
                    <li>1. Upload documents containing process information</li>
                    <li>2. Run document analysis</li>
                    <li>3. Complete process synthesis</li>
                    <li>4. Generate process maps</li>
                  </ol>
                </div>
              </div>
            </div>
            <button
              onClick={() => window.location.href = '/app/documents'}
              className="bg-electric-blue text-white px-6 py-2 rounded-lg hover:bg-darker-blue transition-colors flex items-center space-x-2 mx-auto"
            >
              <Upload className="w-4 h-4" />
              <span>Upload Documents</span>
              <ArrowRight className="w-4 h-4" />
            </button>
          </div>
        </div>
      );
    }

    if (!hasAnalysis && !hasExistingMaps) {
      return (
        <div className="text-center py-12 bg-amber-50 rounded-lg border-2 border-dashed border-amber-300">
          <div className="max-w-md mx-auto">
            <FileText className="w-16 h-16 text-amber-500 mx-auto mb-4" />
            <h3 className="text-xl font-medium text-gray-900 mb-2">Documents Need Analysis</h3>
            <p className="text-gray-600 mb-4">
              You have {documentCount} document{documentCount !== 1 ? 's' : ''} uploaded, but they haven't been analyzed yet.
            </p>
            <div className="bg-amber-100 border border-amber-200 rounded-lg p-4 mb-4">
              <div className="flex items-start space-x-3">
                <AlertCircle className="w-5 h-5 text-amber-600 mt-0.5" />
                <div className="text-left">
                  <p className="text-sm font-medium text-amber-800 mb-1">Next Steps:</p>
                  <ol className="text-sm text-amber-700 space-y-1">
                    <li>1. ✅ Documents uploaded ({documentCount} files)</li>
                    <li>2. 🔄 Run document analysis</li>
                    <li>3. ⏳ Complete process synthesis</li>
                    <li>4. ⏳ Generate process maps</li>
                  </ol>
                </div>
              </div>
            </div>
            <button
              onClick={() => window.location.href = '/app/sop-builder'}
              className="bg-amber-500 text-white px-6 py-2 rounded-lg hover:bg-amber-600 transition-colors flex items-center space-x-2 mx-auto"
            >
              <BarChart3 className="w-4 h-4" />
              <span>Start Analysis</span>
              <ArrowRight className="w-4 h-4" />
            </button>
          </div>
        </div>
      );
    }

    if (!hasSynthesis) {
      if (hasExistingMaps) {
        // If we have existing maps but no synthesis, show a different message
        return (
          <div className="text-center py-12 bg-orange-50 rounded-lg border-2 border-dashed border-orange-300">
            <div className="max-w-md mx-auto">
              <AlertCircle className="w-16 h-16 text-orange-500 mx-auto mb-4" />
              <h3 className="text-xl font-medium text-gray-900 mb-2">New Documents Detected</h3>
              <p className="text-gray-600 mb-4">
                You've uploaded new documents that haven't been analyzed yet. Please analyze all documents and update process synthesis to regenerate accurate process maps.
              </p>
              <div className="bg-orange-100 border border-orange-200 rounded-lg p-4 mb-4">
                <div className="flex items-start space-x-3">
                  <AlertCircle className="w-5 h-5 text-orange-600 mt-0.5" />
                  <div className="text-left">
                    <p className="text-sm font-medium text-orange-800 mb-1">Required Actions:</p>
                    <ol className="text-sm text-orange-700 space-y-1">
                      <li>1. ✅ Documents uploaded ({documentCount} files)</li>
                      <li>2. 🔄 Analyze all documents</li>
                      <li>3. 🔄 Update process synthesis</li>
                      <li>4. 🔄 Regenerate process maps</li>
                    </ol>
                  </div>
                </div>
              </div>
              <div className="flex space-x-3 justify-center">
                <button
                  onClick={() => window.location.href = '/app/sop-builder'}
                  className="bg-orange-500 text-white px-6 py-2 rounded-lg hover:bg-orange-600 transition-colors flex items-center space-x-2"
                >
                  <FileText className="w-4 h-4" />
                  <span>Analyze Documents</span>
                </button>
              </div>
            </div>
          </div>
        );
      }

      // Original synthesis required message for projects without existing maps
      return (
        <div className="text-center py-12 bg-purple-50 rounded-lg border-2 border-dashed border-purple-300">
          <div className="max-w-md mx-auto">
            <BarChart3 className="w-16 h-16 text-purple-500 mx-auto mb-4" />
            <h3 className="text-xl font-medium text-gray-900 mb-2">Process Synthesis Required</h3>
            <p className="text-gray-600 mb-4">
              Documents have been analyzed, but process synthesis hasn't been completed yet. Process maps depend on synthesis results.
            </p>
            <div className="bg-purple-100 border border-purple-200 rounded-lg p-4 mb-4">
              <div className="flex items-start space-x-3">
                <AlertCircle className="w-5 h-5 text-purple-600 mt-0.5" />
                <div className="text-left">
                  <p className="text-sm font-medium text-purple-800 mb-1">Progress:</p>
                  <ol className="text-sm text-purple-700 space-y-1">
                    <li>1. ✅ Documents uploaded ({documentCount} files)</li>
                    <li>2. ✅ Document analysis completed</li>
                    <li>3. 🔄 Complete process synthesis</li>
                    <li>4. ⏳ Generate process maps</li>
                  </ol>
                </div>
              </div>
            </div>
            <button
              onClick={() => window.location.href = '/app/sop-builder'}
              className="bg-purple-500 text-white px-6 py-2 rounded-lg hover:bg-purple-600 transition-colors flex items-center space-x-2 mx-auto"
            >
              <BarChart3 className="w-4 h-4" />
              <span>Run Process Synthesis</span>
              <ArrowRight className="w-4 h-4" />
            </button>
          </div>
        </div>
      );
    }

    if (loadingRoleAssignments) {
      return (
        <div className="text-center py-12">
          <div className="animate-spin rounded-full h-16 w-16 border-b-2 border-electric-blue mx-auto mb-4"></div>
          <h3 className="text-xl font-medium text-gray-900 mb-2">Loading Role Assignments</h3>
          <p className="text-gray-500 mb-4 max-w-md mx-auto">
            Generating intelligent role assignments for process steps...
          </p>
        </div>
      );
    }

    const hasValidProcessMap = currentMap && currentMap.map_data;

    return (
      <div className="space-y-2" style={{ width: '100%', maxWidth: '100%', overflow: 'hidden' }}>

        <div className="bg-white border border-gray-200 rounded-xl shadow-sm">
          <div className="flex items-center justify-between px-3 py-2 flex-wrap gap-2">
            <div className="flex items-center flex-wrap gap-3">
              {(() => {
                const levels = [
                  { value: 1, name: 'L1 Strategic', icon: '🎯' },
                  { value: 3, name: 'L3 Operational', icon: '🔧' },
                  { value: 2, name: 'L2 Tactical', icon: '⚡' },
                  { value: 4, name: 'L4 Execution', icon: '⚙️' }
                ];
                const existingMap = allProcessMaps.find(map => map.level === selectedLevel);
                const hasMap = !!existingMap;
                const canGen = (() => {
                  if (!hasAllPrerequisites) return false;
                  if (selectedLevel === 3 || selectedLevel === 1) return true;
                  if (selectedLevel === 4 || selectedLevel === 2) return allProcessMaps.some(map => map.level === 3);
                  return false;
                })();

                return (
                  <>
                    <div className="inline-flex bg-gray-100 rounded-lg p-0.5">
                      {levels.map((level) => {
                        const isSelected = selectedLevel === level.value;
                        const levelHasMap = allProcessMaps.some(map => map.level === level.value);
                        return (
                          <button
                            key={level.value}
                            onClick={() => handleLevelChange(level.value)}
                            className={`relative px-2 py-1 rounded-md text-xs font-medium transition-all flex items-center space-x-1 ${
                              isSelected
                                ? 'bg-white text-super-dark-purple shadow-sm border border-gray-200'
                                : levelHasMap
                                  ? 'text-gray-600 hover:text-gray-900 hover:bg-gray-50'
                                  : 'text-gray-400 hover:text-gray-600 hover:bg-gray-50'
                            }`}
                          >
                            <span>{level.name}</span>
                            {levelHasMap && (
                              <span className="w-1.5 h-1.5 bg-green-500 rounded-full" />
                            )}
                          </button>
                        );
                      })}
                    </div>
                  </>
                );
              })()}

              {user?.role === 'SUPER_ADMIN' && <div className="w-px h-6 bg-gray-300" />}
              {user?.role === 'SUPER_ADMIN' && (
                <div className="inline-flex bg-gray-100 rounded-lg p-0.5">
                  <button
                    onClick={() => setDiagramMode('interactive')}
                    className={`px-2 py-1 rounded-md text-xs font-medium transition-all flex items-center space-x-1 ${
                      diagramMode === 'interactive'
                        ? 'bg-white text-electric-blue shadow-sm border border-gray-200'
                        : 'text-gray-500 hover:text-gray-900'
                    }`}
                  >
                    <Eye className="w-3 h-3" />
                    <span>Interactive</span>
                  </button>
                  <button
                    onClick={() => setDiagramMode('professional')}
                    className={`px-2 py-1 rounded-md text-xs font-medium transition-all flex items-center space-x-1 ${
                      diagramMode === 'professional'
                        ? 'bg-white text-purple-600 shadow-sm border border-gray-200'
                        : 'text-gray-500 hover:text-gray-900'
                    }`}
                  >
                    <Zap className="w-3 h-3" />
                    <span>Professional</span>
                  </button>
                </div>
              )}

              {/* Edit moved into ProcessMapValidationBar (step 3) */}

              {currentMap && <div className="w-px h-6 bg-gray-300" />}
              {currentMap && (
                <div className="inline-flex bg-gray-100 rounded-lg p-0.5">
                  <ToolbarDropdown
                    label="Analysis"
                    icon={<BarChart3 className="w-3 h-3" />}
                    keepOpenOnClick={true}
                    showActiveIndicator={true}
                    items={[
                      {
                        key: 'gap-status',
                        label: 'Gap Status',
                        icon: <BarChart3 className="w-4 h-4" />,
                        onClick: () => setShowGapStatistics(!showGapStatistics),
                        active: showGapStatistics,
                        helpContent: {
                          title: 'Gap Detection',
                          body: (
                            <ul className="list-disc pl-3 space-y-1">
                              <li>Maps each step against regulatory requirements and your control framework</li>
                              <li>Detects missing, redundant, and overlapping controls</li>
                              <li>Risk-weighted severity scoring</li>
                            </ul>
                          ),
                        },
                      },
                      {
                        key: 'friction-heatmap',
                        label: 'Friction Heatmap',
                        icon: <Zap className="w-4 h-4" />,
                        onClick: handleToggleFrictionHeatmap,
                        active: showFrictionHeatmap,
                        loading: frictionLoading,
                        disabled: frictionLoading,
                        helpContent: {
                          title: 'Friction Analysis Engine',
                          body: (
                            <ul className="list-disc pl-3 space-y-1">
                              <li>Scores each step across 5 dimensions: error rates, manual effort, cycle time, handoffs, exceptions</li>
                              <li>Industry benchmarking calibrates thresholds for your process type</li>
                              <li>Correlates friction hotspots with downstream impact</li>
                            </ul>
                          ),
                        },
                      },
                      {
                        key: 'heatmap-chart',
                        label: 'Heatmap Chart',
                        icon: <BarChart3 className="w-4 h-4" />,
                        onClick: () => setShowHeatmapChart(v => !v),
                        active: showHeatmapChart,
                      },
                      {
                        key: 're-estimate-friction',
                        label: frictionEstimating ? 'Estimating...' : 'Re-estimate All',
                        icon: <Zap className="w-4 h-4" />,
                        onClick: async () => {
                          if (currentMap?.id) await estimateFriction(currentMap.id);
                        },
                        disabled: frictionEstimating || frictionLoading,
                        loading: frictionEstimating,
                        hidden: !showFrictionHeatmap,
                        divider: true,
                      },
                    ]}
                  />
                </div>
              )}

              {currentMap && diagramMode === 'interactive' && <div className="w-px h-6 bg-gray-300" />}
              {currentMap && diagramMode === 'interactive' && (
                <div className="inline-flex items-center bg-gray-100 rounded-lg p-0.5 space-x-0.5">
                  <button
                    onClick={() => handleZoomChange(zoomLevel - 10)}
                    disabled={zoomLevel <= 25}
                    className="p-1 rounded-md text-gray-600 hover:bg-white hover:shadow-sm disabled:opacity-40 disabled:cursor-not-allowed"
                  >
                    <Minus className="w-3 h-3" />
                  </button>
                  <span className="text-xs text-gray-600 font-medium w-7 text-center">{zoomLevel}%</span>
                  <button
                    onClick={() => handleZoomChange(zoomLevel + 10)}
                    disabled={zoomLevel >= 200}
                    className="p-1 rounded-md text-gray-600 hover:bg-white hover:shadow-sm disabled:opacity-40 disabled:cursor-not-allowed"
                  >
                    <Plus className="w-3 h-3" />
                  </button>
                </div>
              )}

              {/* Run Verifier + Export moved into ProcessMapValidationBar (steps 1 and 5) */}
            </div>

            <div className="flex items-center gap-2">
              {(() => {
                const existingMap = allProcessMaps.find(map => map.level === selectedLevel);
                const hasMap = !!existingMap;
                const canGen = (() => {
                  if (!hasAllPrerequisites) return false;
                  if (selectedLevel === 3 || selectedLevel === 1) return true;
                  if (selectedLevel === 4 || selectedLevel === 2) return allProcessMaps.some(map => map.level === 3);
                  return false;
                })();
                return hasAllPrerequisites ? (
                  generating ? (
                    <button
                      type="button"
                      onClick={(e) => {
                        e.stopPropagation();
                        stopMapGeneration();
                      }}
                      disabled={cancellingGeneration}
                      className="text-xs px-2.5 py-1.5 rounded-lg transition-colors flex items-center space-x-1 font-medium bg-white text-slate-700 border border-slate-300 hover:bg-slate-50 disabled:opacity-50"
                      title="Stop generation"
                    >
                      {cancellingGeneration ? (
                        <div className="w-3 h-3 border border-slate-400 border-t-transparent rounded-full animate-spin" />
                      ) : (
                        <Square className="w-3 h-3" />
                      )}
                      <span>Stop</span>
                    </button>
                  ) : (
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      if (canGen) {
                        if (hasMap) {
                          setShowLoader(true);
                          setLoaderOperation('regenerating');
                          setLoaderProgress(0);
                          setLoaderStage('Preparing to regenerate...');
                        }
                        generateMapForLevel(selectedLevel);
                      }
                    }}
                    disabled={!canGen}
                    className={`text-xs px-2.5 py-1.5 rounded-lg transition-colors flex items-center space-x-1 font-medium ${
                      canGen
                        ? hasMap
                          ? 'bg-amber-500 text-white hover:bg-amber-600'
                          : 'bg-electric-blue text-white hover:bg-darker-blue'
                        : 'bg-gray-100 text-gray-400 cursor-not-allowed'
                    }`}
                  >
                    {hasMap ? (
                      <>
                        <RefreshCw className="w-3 h-3" />
                        <span>Regenerate</span>
                      </>
                    ) : (
                      <>
                        <Play className="w-3 h-3" />
                        <span>Generate</span>
                      </>
                    )}
                  </button>
                  )
                ) : null;
              })()}
              {/* Validate moved into ProcessMapValidationBar as "Run Challenger Model" (step 2) */}
            </div>
          </div>
        </div>

        {!hasValidProcessMap ? (
          <div className="text-center py-12">
            <BarChart3 className="w-12 h-12 text-gray-300 mx-auto mb-3" />
            <h3 className="text-lg font-medium text-gray-900 mb-1">
              No Process Map for Level {selectedLevel}: {processLevels.find(l => l.value === selectedLevel)?.name}
            </h3>
            <p className="text-sm text-gray-500 mb-4 max-w-sm mx-auto">
              {processLevels.find(l => l.value === selectedLevel)?.description}
            </p>
            {hasAllPrerequisites ? (
              generating ? (
                <button
                  type="button"
                  onClick={stopMapGeneration}
                  disabled={cancellingGeneration}
                  className="bg-white text-slate-700 border border-slate-300 px-5 py-2 rounded-lg hover:bg-slate-50 transition-colors disabled:opacity-50 flex items-center space-x-2 mx-auto text-sm"
                >
                  <Square className="w-4 h-4" />
                  <span>{cancellingGeneration ? 'Stopping...' : 'Stop generation'}</span>
                </button>
              ) : (
              <button
                onClick={generateMap}
                className="bg-electric-blue text-white px-5 py-2 rounded-lg hover:bg-darker-blue transition-colors flex items-center space-x-2 mx-auto text-sm"
              >
                <Play className="w-4 h-4" />
                <span>{`Generate Level ${selectedLevel} Map`}</span>
              </button>
              )
            ) : (
              <span className="text-xs text-amber-700 bg-amber-50 border border-amber-200 px-3 py-1.5 rounded-lg inline-flex items-center space-x-1.5">
                <AlertCircle className="w-3.5 h-3.5" />
                <span>Complete analysis & synthesis first</span>
              </span>
            )}
          </div>
        ) : (
          <>
            {validating && validationProgress && (
              <div className="mt-4 bg-blue-50 border border-blue-200 rounded-lg p-3">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-sm font-medium text-blue-800">
                    Validation in progress...
                  </span>
                  <span className="text-sm text-blue-600 font-semibold">
                    {validationStepLabel(
                      validationProgress,
                      resolveTotalSteps(validationProgress, countMapSteps(currentMap?.map_data)),
                    )}
                  </span>
                </div>
                {validationPercent(
                  validationProgress,
                  resolveTotalSteps(validationProgress, countMapSteps(currentMap?.map_data)),
                ) !== null && (
                  <div className="w-full bg-blue-200 rounded-full h-2">
                    <div
                      className="bg-blue-600 h-2 rounded-full transition-all duration-500"
                      style={{
                        width: `${validationPercent(
                          validationProgress,
                          resolveTotalSteps(validationProgress, countMapSteps(currentMap?.map_data)),
                        )}%`,
                      }}
                    />
                  </div>
                )}
                <p className="text-xs text-blue-700 mt-2">
                  {validationDetailLine(
                    validationProgress,
                    resolveTotalSteps(validationProgress, countMapSteps(currentMap?.map_data)),
                  )}
                </p>
              </div>
            )}

            {showHeatmapChart && currentMap && (
              <div className="mt-4">
                <FrictionHeatmapChart
                  processMapId={currentMap.id}
                  processMapName={currentMap.process_name}
                />
              </div>
            )}

            <div className="relative" style={{ width: '100%', maxWidth: '100%', minHeight: '600px' }}>
              <div>
                {diagramMode === 'interactive' ? (
                  <div className="relative">
                    <SwimlanesVisualization
                      key={`${currentMap?.id}-${selectedLevel}-${currentMap?.updated_at}-${mapRefreshCounter}`}
                      swimlanes={swimlanes}
                      processes={processes}
                      selectedLevel={selectedLevel}
                      analysisResults={analysisResults}
                      showAuditInfo={true}
                      currentMap={currentMap}
                      auditEnhancementStatus={auditEnhancementStatus}
                      zoomLevel={zoomLevel}
                      onZoomChange={handleZoomChange}
                      isEditMode={isEditMode}
                      onProcessReorder={handleProcessReorder}
                      onDecisionEdit={handleDecisionEdit}
                      onFindingAction={handleFindingAction}
                      findings={mergeFindingStatuses(
                        currentMap?.generation_metadata?.accuracy_gate?.findings,
                        findingSessionStatus,
                      )}
                      sourceHasDiamonds={sourceHasDiamonds(currentMap?.generation_metadata?.accuracy_gate)}
                      extraStakeholders={stakeholderNamesFromMap(editedMapData || currentMap?.map_data)}
                      onStepDelete={handleStepDelete}
                      onPendingDeleteChange={handlePendingDeleteChange}
                      showFrictionHeatmap={showFrictionHeatmap}
                      frictionDataMap={frictionMap}
                      onExportReady={setExportFns}
                    />
                    <FrictionLegend isVisible={!!(showFrictionHeatmap && frictionData?.steps?.some(s => s.has_data))} />
                  </div>
                ) : (
                  <DrawIOClientEditor
                    mapId={currentMap?.id || 0}
                    mapTitle={currentMap?.title || `Level ${selectedLevel} Process Map`}
                  />
                )}
              </div>

              {currentMap && (
                <div
                  className={`absolute top-0 right-0 h-full bg-white shadow-2xl border-l border-gray-200 overflow-y-auto transition-transform duration-300 ease-in-out ${
                    showGapStatistics ? 'translate-x-0' : 'translate-x-full'
                  }`}
                  style={{
                    width: '400px',
                    maxWidth: '90vw',
                    zIndex: 50
                  }}
                >
                  <ProcessMapStatistics
                    processes={processes}
                    elements={currentMap?.map_data?.elements}
                    currentMap={currentMap}
                    selectedLevel={selectedLevel}
                    onClose={() => setShowGapStatistics(false)}
                  />
                </div>
              )}

              {showGapStatistics && (
                <div
                  className="absolute inset-0 bg-black bg-opacity-30 transition-opacity duration-300"
                  style={{ zIndex: 40 }}
                  onClick={() => setShowGapStatistics(false)}
                />
              )}
            </div>
          </>
        )}
      </div>
    );
  };



  useEffect(() => {
    const mapForLevel = allProcessMaps.find(map => map.level === selectedLevel);
    setCurrentMap(mapForLevel || null);

    if (showCommandCenter && currentMap?.level !== selectedLevel) {
      setShowCommandCenter(false);
    }
  }, [allProcessMaps, selectedLevel, currentProject?.id]);


  const hasAllPrerequisites = hasDocuments && hasAnalysis && hasSynthesis;

  useEffect(() => {
    if (currentProject) {
      loadProcessMaps();
    }
  }, [currentProject, loadProcessMaps]);

  useEffect(() => {
    if (currentProject && selectedLevel === 1 && hasAllPrerequisites) {
      const { processes } = getProcessData();
      if (processes.length > 0) {
        setEnhancedProcesses(processes);
      }
    }
  }, [currentProject, selectedLevel, hasAllPrerequisites]);

  // Only clear data when heatmap is toggled OFF
  useEffect(() => {
    if (!showFrictionHeatmap) {
      clearFrictionData();
    }
  }, [showFrictionHeatmap, clearFrictionData]);

  const handleToggleFrictionHeatmap = useCallback(async () => {
    const turningOn = !showFrictionHeatmap;
    setShowFrictionHeatmap(turningOn);

    if (turningOn && currentMap?.id) {
      // Step 1: Fetch existing data — use returned count to avoid stale state race condition
      const existingCount = await fetchFrictionData(currentMap.id);

      // Step 2: Only estimate if no data exists (use direct return value, not state)
      if (existingCount === 0) {
        await estimateFriction(currentMap.id);
      }
    }
  }, [showFrictionHeatmap, currentMap?.id, estimateFriction, fetchFrictionData]);

  const handleEstimateFriction = useCallback(async () => {
    if (currentMap?.id) {
      await estimateFriction(currentMap.id);
    }
  }, [currentMap?.id, estimateFriction]);

  // ---------------------------------------------------------------------------
  // Validation chain (see ProcessMapValidationBar for the ordering rationale)
  // ---------------------------------------------------------------------------

  const handleRunVerifier = useCallback(async () => {
    if (!currentMap?.id || isVerifyingMap) return;
    setIsVerifyingMap(true);
    const toastId = 'verify-process-map';
    try {
      toast.loading('Running accuracy gate...', { id: toastId, duration: Infinity });
      const enqueueRes = await api.post(`/api/analysis/process-maps/${currentMap.id}/verify`, { mode: 'standard' });
      const data = enqueueRes.data || {};
      if (data.status === 'completed' && data.accuracy_gate) {
        toast.success(accuracyGateHeadline(data.accuracy_gate), { id: toastId, duration: 4000 });
      } else {
        const jobId = String(data.job_id || '');
        if (!jobId) throw new Error('Backend did not return a job id');
        await pollVerificationJob(jobId, {
          onProgress: (s) => {
            toast.loading(describeProgress(s.progress), { id: toastId, duration: Infinity });
          }
        });
        toast.success('Verification complete', { id: toastId, duration: 3000 });
      }
      await loadProcessMaps();
    } catch (error: any) {
      console.error('Verify process map error:', error);
      if (error?.response?.status === 404) {
        toast.error('This process map is out of date — refreshing the view, please try again', { id: toastId });
        await loadProcessMaps();
      } else {
        showApiError(error, 'Failed to run verifier', { id: toastId });
      }
    } finally {
      setIsVerifyingMap(false);
    }
  }, [currentMap?.id, isVerifyingMap, loadProcessMaps]);

  /**
   * Audit trail is export-only: it is no longer rendered inline on the map.
   * The backend builds the enhanced payload on demand here (see
   * AUDIT_ENHANCEMENT_MODE) so map generation is not slowed by LLM calls
   * whose output most reviewers never open.
   */
  const handleExportAuditTrail = useCallback(async (format: 'pdf' | 'csv') => {
    if (!currentMap?.id || exportingAudit) return;
    setExportingAudit(true);
    const toastId = 'export-audit-trail';
    try {
      toast.loading('Building audit trail — this runs the full reasoning pass over every step and can take 10-15 minutes the first time. It is cached afterwards, so later exports are quick.', {
        id: toastId,
        duration: Infinity,
      });

      // The first export of a map has to wait for the reasoning pass to finish:
      // measured at 8m15s for a 12-step map and 14m32s for a 19-step one. A
      // single request cannot survive that. Backend logs show the connection
      // being dropped roughly every thirty seconds and the browser silently
      // re-issuing the GET, until it gave up and reported "Could not reach the
      // server" — while the build ran to completion server-side and was cached.
      //
      // So retry deliberately instead of relying on one long-held socket. The
      // backend joins an in-flight build rather than starting a second one, and
      // serves the cached result once it is ready, so each retry is cheap and
      // the one that lands after completion returns the finished file.
      const deadline = Date.now() + 20 * 60 * 1000;
      const isTransient = (err: any) =>
        !err?.response || /timeout of \d+ms exceeded/i.test(err?.message || '');

      let response;
      let attempt = 0;
      // eslint-disable-next-line no-constant-condition
      while (true) {
        try {
          response = await api.get(
            `/api/analysis/process-maps/${currentMap.id}/audit-trail/${format}`,
            { params: format === 'pdf' ? { includeTechnicalDetails: true } : undefined, responseType: 'blob', timeout: 300000 }
          );
          break;
        } catch (attemptError: any) {
          if (!isTransient(attemptError) || Date.now() >= deadline) throw attemptError;
          attempt += 1;
          const waitedMinutes = Math.round((20 * 60 * 1000 - (deadline - Date.now())) / 60000);
          toast.loading(
            `Still building the audit trail on the server — reconnecting automatically (attempt ${attempt}, ${waitedMinutes} min so far). The download starts on its own once it is ready.`,
            { id: toastId, duration: Infinity },
          );
          await new Promise(resolve => setTimeout(resolve, 5000));
        }
      }

      const blob = new Blob([response.data], {
        type: format === 'pdf' ? 'application/pdf' : 'text/csv',
      });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `process-map-L${currentMap.level}-audit-trail.${format}`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      toast.success('Audit trail downloaded', { id: toastId, duration: 3000 });
    } catch (error: any) {
      console.error('Audit trail export error:', error);
      // Reaching here after the retry window means the build is still going.
      // The shared handler would call that "Could not reach the server", which
      // is untrue and sends the reader looking for a network fault: the build
      // keeps running and its result is cached on completion, so exporting
      // again shortly afterwards returns the finished file immediately.
      if (!error?.response || /timeout of \d+ms exceeded/i.test(error?.message || '')) {
        toast.error(
          'The audit trail is taking longer than 20 minutes to build. It is still running on the server and is cached when it finishes — try the export again in a few minutes.',
          { id: toastId, duration: 8000 },
        );
      } else {
        await showApiErrorAsync(error, 'Failed to export audit trail', { id: toastId });
      }
    } finally {
      setExportingAudit(false);
    }
  }, [currentMap?.id, currentMap?.level, exportingAudit]);

  const handleExportValidationReport = useCallback(async () => {
    if (!validationId || exportingValidationReport) return;
    setExportingValidationReport(true);
    const toastId = 'export-validation-report';
    try {
      toast.loading('Building validation report...', { id: toastId, duration: Infinity });
      const response = await api.get(
        `/api/validation/validation/${validationId}/download-excel`,
        { responseType: 'blob', timeout: 600000 }
      );
      const blob = new Blob([response.data], {
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `process-map-L${currentMap?.level}-validation-report.xlsx`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      toast.success('Validation report downloaded', { id: toastId, duration: 3000 });
    } catch (error: any) {
      console.error('Validation report export error:', error);
      await showApiErrorAsync(error, 'Failed to export validation report', { id: toastId });
    } finally {
      setExportingValidationReport(false);
    }
  }, [validationId, currentMap?.level, exportingValidationReport]);

  const handleRunChallenger = useCallback(() => {
    if (currentMap?.level !== 3) {
      toast.error('The Challenger Model is only available for Level 3 (Operational) process maps');
      return;
    }
    if (validationId) {
      setShowCommandCenter(true);
    } else if (!validating) {
      startValidationFromButton();
    }
    // currentMap?.id is in the deps because startValidationFromButton closes over
    // the current map — a regenerate at the same level swaps the map id and would
    // otherwise POST against a stale one.
  }, [currentMap?.id, currentMap?.level, validationId, validating]);

  const validationSteps: ValidationStepDef[] = useMemo(() => {
    if (!currentMap) return [];

    const isApproved = approvalInfo?.validation_status === 'validated' && approvalInfo?.id === currentMap.id;
    const verifierDone = isAccuracyGateDone(currentMap?.generation_metadata?.accuracy_gate);
    const challengerDone = !!validationId;
    const challengerAvailable =
      currentMap.level === 3 && !currentMap?.map_data?.metadata?.preserve_mode;
    const canEdit = currentMap.level === 3 || currentMap.level === 1;

    // Approval acts on whichever map SOP Builder considers authoritative for the
    // project, which is not necessarily the level tab currently on screen. Gating
    // steps 1-2 against `currentMap` while approving a different map would let a
    // reviewer verify L1 and approve an unverified L3, so the approve control is
    // only live when the two refer to the same map.
    const approveTargetsCurrentMap = !!approvalInfo && approvalInfo.id === currentMap.id;

    // Deliberately NOT a hard block. The verifier no longer depends on a linked
    // SOP, so the original reason for the soft gate is gone, but a reviewer can
    // still have legitimate cause to approve a map the challenger has not seen
    // (L1/L2 maps have no challenger at all). The unmet checks are named in the
    // tooltip and confirmed on click — the sequence is advice, not a cage.
    const outstandingChecks: string[] = [];
    if (!verifierDone) outstandingChecks.push('Run Verifier');
    if (challengerAvailable && !challengerDone) outstandingChecks.push('Run Challenger Model');

    const confirmApprove = () => {
      if (!onApprove) return;

      if (outstandingChecks.length) {
        setConfirmRequest({
          title: 'Approve without running every check?',
          message:
            `${outstandingChecks.join(' and ')} ${outstandingChecks.length > 1 ? 'have' : 'has'} not been run on this map.\n\n` +
            'Approving locks the map and unlocks the next step in SOP Builder.',
          confirmLabel: 'Approve anyway',
          cancelLabel: 'Run the checks first',
          onConfirm: onApprove,
        });
        return;
      }

      onApprove();
    };

    const status = (s: ValidationStepStatus) => s;

    const steps: ValidationStepDef[] = [
      {
        id: 'verify',
        label: 'Run Verifier',
        runningLabel: 'Verifying...',
        icon: <ShieldCheck className="w-3 h-3" />,
        status: isVerifyingMap
          ? status('running')
          : verifierDone
            ? status('done')
            : status('active'),
        tooltip: verifierDone
          ? 'Accuracy gate has run. Findings (if any) are proposed overlays for Edit — a failed pack still counts as done. Re-run after any edit.'
          : 'Step 1 — deterministic accuracy gate: did Jarvis reproduce the source diagram (boxes, actors, tracks, START/END, edges, YES/NO), including omitted source steps. Not a claim-by-claim check against Process Synthesis.',
        onClick: handleRunVerifier,
      },
      {
        id: 'challenger',
        label: 'Run Challenger Model',
        runningLabel: 'Challenging...',
        icon: <Zap className="w-3 h-3" />,
        status: validating
          ? status('running')
          : !challengerAvailable
            ? status('blocked')
            : challengerDone
              ? status('done')
              : verifierDone
                ? status('active')
                : status('pending'),
        tooltip: !challengerAvailable
          ? currentMap.level !== 3
            ? 'The Challenger Model runs on Level 3 (Operational) maps only.'
            : 'Not available for preserved maps.'
          : challengerDone
            ? 'Open the Step Validation panel to review the challenger findings. The full report is under Export → Validation Report.'
            : 'Step 2 — independent second-opinion model reviews each step against your source documents. Expensive, so run it on a map that is already structurally clean.',
        onClick: handleRunChallenger,
        // Replaces the in-page Validation Report panel: the reviewer still sees
        // that a validation exists and how it went, without a panel below the map.
        trailing: challengerDone && validationSummary ? (
          <button
            type="button"
            onClick={() => setShowCommandCenter(true)}
            title="Challenger result. Click to review it step by step, or use Export → Validation Report for the full breakdown."
            className="inline-flex items-center gap-1.5 px-2 py-1 rounded-full border border-green-200 bg-green-50 text-[11px] font-medium text-green-800 hover:bg-green-100 transition-colors"
          >
            {validationSummary.match_percentage !== null && (
              <span>{validationSummary.match_percentage}% match</span>
            )}
            {validationSummary.needs_review > 0 && (
              <>
                <span className="text-green-300">|</span>
                <span className="text-amber-700">{validationSummary.needs_review} need review</span>
              </>
            )}
          </button>
        ) : undefined,
      },
      {
        id: 'edit',
        // Label stays "Cancel Edit" while editing because the handler discards
        // unsaved changes (after a confirm). "Done Editing" would invite the
        // destructive answer. Committing changes is the separate Save button.
        label: isEditMode ? 'Cancel Edit' : 'Edit',
        icon: isEditMode ? <X className="w-3 h-3" /> : <Edit3 className="w-3 h-3" />,
        status: isEditMode
          ? status('active')
          : !canEdit
            ? status('blocked')
            : (verifierDone || challengerDone)
              ? status('active')
              : status('pending'),
        tooltip: !canEdit
          ? 'Editing is available for Level 1 and Level 3 maps.'
          : isEditMode
            ? 'Discard edit mode. Unsaved changes are lost — use Save to keep them.'
            : 'Step 3 — reconcile what the verifier and challenger flagged. Manual edits take precedence and survive re-validation.',
        disabled: generating || loading,
        onClick: handleToggleEditMode,
        trailing:
          isEditMode && hasUnsavedChanges ? (
            <button
              onClick={handleSaveChanges}
              disabled={savingChanges}
              className="px-2 py-1 rounded-lg text-xs font-medium bg-electric-blue text-white border border-electric-blue shadow-sm flex items-center gap-1 disabled:opacity-50"
            >
              {savingChanges ? (
                <div className="w-3 h-3 border-2 border-white border-t-transparent rounded-full animate-spin" />
              ) : (
                <Save className="w-3 h-3" />
              )}
              <span>{savingChanges ? 'Saving...' : 'Save'}</span>
            </button>
          ) : undefined,
      },
      {
        id: 'approve',
        label: isApproved ? 'Revoke Approval' : 'Approve Map',
        runningLabel: isApproved ? 'Revoking...' : 'Approving...',
        icon: <Check className="w-3 h-3" />,
        tone: 'approve',
        status: approving
          ? status('running')
          : isApproved
            ? status('done')
            : !approvalInfo
              ? status('blocked')
              : !approveTargetsCurrentMap
                ? status('blocked')
                : outstandingChecks.length
                  ? status('pending')
                  : status('active'),
        tooltip: isApproved
          ? `Approved${approvalInfo?.approved_at ? ` on ${new Date(approvalInfo.approved_at).toLocaleString()}` : ''}. Editing steps, stakeholders, or the map resets this automatically.`
          // Reaching this branch means a map is on screen but no approvable one
          // came back for the project, so "generate a process map first" would
          // contradict what the reader is looking at.
          : !approvalInfo
            ? 'No approvable map found for this project yet.'
            : !approveTargetsCurrentMap
              ? `Approval applies to the Level ${approvalInfo.level} map. Switch to that level to approve.`
              : outstandingChecks.length
                ? `Step 4 — locks the map and unlocks the next SOP Builder step. ${outstandingChecks.join(' and ')} ${outstandingChecks.length > 1 ? 'have' : 'has'} not been run yet; you will be asked to confirm.`
                : 'Step 4 — locks the map and unlocks the next step in SOP Builder.',
        onClick: isApproved ? onRevokeApproval : confirmApprove,
      },
      {
        id: 'export',
        label: 'Export',
        icon: <Download className="w-3 h-3" />,
        status: isApproved ? status('active') : status('pending'),
        tooltip:
          'Step 5 — download the map and its audit trail. The audit trail package is the record auditors ask for; it is generated on demand here rather than during map generation.',
        render: () => (
          <ToolbarDropdown
            label="Export"
            icon={<Download className="w-3 h-3" />}
            dropdownPosition="bottom-right"
            portalMenu
            headerHelp={{
              title: 'Export Formats',
              body: (
                <ul className="list-disc pl-3 space-y-1 mt-1">
                  <li>Excel: Full step data with BPMN metadata and swim lane assignments</li>
                  <li>PNG/PDF: High-res visual capture of your process map</li>
                  <li>BPMN 2.0 XML: Industry-standard, compatible with Camunda, Flowable, bpmn.io</li>
                  <li>Validation Report: the Challenger findings — match rate, clear matches, inferences, ambiguities and gaps</li>
                  <li>Audit Trail: per-step reasoning, source citations, and cross-validations</li>
                </ul>
              ),
            }}
            items={[
              {
                key: 'excel',
                label: 'Export to Excel',
                icon: <FileText className="w-4 h-4" />,
                onClick: () => exportFns?.downloadExcel(),
                disabled: !exportFns || exportFns.isDownloading,
              },
              {
                key: 'png',
                label: 'Download as PNG',
                icon: <Image className="w-4 h-4" />,
                onClick: () => exportFns?.downloadPNG(),
                disabled: !exportFns || exportFns.isDownloading,
              },
              {
                key: 'pdf',
                label: 'Download as PDF',
                icon: <Download className="w-4 h-4" />,
                onClick: () => exportFns?.downloadPDF(),
                disabled: !exportFns || exportFns.isDownloading,
              },
              {
                key: 'bpmn',
                label: 'Export BPMN XML',
                icon: <Code className="w-4 h-4" />,
                onClick: () => exportFns?.downloadBPMN(),
                disabled: !exportFns || exportFns.isDownloading,
              },
              {
                key: 'validation-report',
                label: exportingValidationReport ? 'Building validation report...' : 'Validation Report (Excel)',
                icon: <Zap className="w-4 h-4" />,
                onClick: handleExportValidationReport,
                disabled: !validationId || exportingValidationReport,
                divider: true,
              },
              {
                key: 'audit-pdf',
                label: exportingAudit ? 'Building audit trail...' : 'Audit Trail (PDF)',
                icon: <ShieldCheck className="w-4 h-4" />,
                onClick: () => handleExportAuditTrail('pdf'),
                disabled: !currentMap?.id || exportingAudit,
              },
              {
                key: 'audit-csv',
                label: exportingAudit ? 'Building audit trail...' : 'Audit Trail (CSV)',
                icon: <FileText className="w-4 h-4" />,
                onClick: () => handleExportAuditTrail('csv'),
                disabled: !currentMap?.id || exportingAudit,
              },
            ]}
          />
        ),
      },
    ];

    // On the standalone /app/process-maps route there is no approval owner, so
    // the approve step is dropped rather than rendered permanently dead.
    return onApprove ? steps : steps.filter(s => s.id !== 'approve');
  }, [
    currentMap,
    approvalInfo,
    approving,
    onApprove,
    onRevokeApproval,
    validationId,
    validating,
    isVerifyingMap,
    isEditMode,
    hasUnsavedChanges,
    savingChanges,
    generating,
    loading,
    exportFns,
    exportingAudit,
    exportingValidationReport,
    validationSummary,
    handleRunVerifier,
    handleRunChallenger,
    handleToggleEditMode,
    handleSaveChanges,
    handleExportAuditTrail,
    handleExportValidationReport,
  ]);

  const isDirectProcessMapsRoute = typeof window !== 'undefined'
    && window.location.pathname.startsWith('/app/process-maps');
  const orchestratorBanner = orchestratorCtx.inRouter && isDirectProcessMapsRoute ? (
    <>
    <OrchestratorProjectMismatchBanner />
    <SmartRouterBanner
      stepLabel="Process Maps"
      canFinish={!!(currentMap && (currentMap as any).validation_status === 'validated')}
      disabledReason="Approve the process map before finishing."
      buildCompletionPayload={() => {
        const procList: any[] = Array.isArray(enhancedProcesses) && enhancedProcesses.length > 0
          ? enhancedProcesses
          : ((currentMap as any)?.processes || []);
        const rows = procList.map((p: any, i: number) => [
          String(p?.step_number || i + 1),
          p?.name || p?.step_title || p?.title || 'Step',
          p?.stakeholder || p?.actor || p?.role || '',
          p?.description || p?.action || '',
          p?.validation_status || '',
        ]);
        const findings = procList.slice(0, 30).map((p: any, i: number) => ({
          title: `Step ${p?.step_number || i + 1}: ${p?.name || p?.step_title || p?.title || 'Step'}`,
          detail: p?.description || p?.action || '',
          evidence: p?.stakeholder || p?.actor || '',
        }));
        const narrative = procList.map((p: any, i: number) => {
          const num = p?.step_number || i + 1;
          const name = p?.name || p?.step_title || p?.title || 'Step';
          const actor = p?.stakeholder || p?.actor || p?.role || '';
          const desc = p?.description || p?.action || '';
          return `### ${num}. ${name}${actor ? ` — ${actor}` : ''}\n\n${desc}`;
        }).join('\n\n');
        const verifiableText = procList.map((p: any, i: number) => {
          const num = p?.step_number || i + 1;
          const actor = p?.stakeholder || p?.actor || p?.role || 'Actor';
          const desc = p?.description || p?.action || p?.name || '';
          return `${num}. [${actor}] ${desc}`;
        }).join('\n');
        return {
          artifact: {
            refs: [{
              kind: 'process_map',
              id: currentMap?.id,
              level: currentMap?.level,
              label: `Process map L${currentMap?.level}`,
            }],
            output_payload: {
              process_map_id: currentMap?.id,
              level: currentMap?.level,
              validation_status: (currentMap as any)?.validation_status || null,
              total_steps: procList.length,
              analysis: {
                narrative,
                findings,
                tables: rows.length > 0
                  ? [{ title: `Process Map L${currentMap?.level ?? ''}`, columns: ['#', 'Step', 'Stakeholder', 'Description', 'Status'], rows }]
                  : [],
                gaps: [],
              },
              analysis_narrative: narrative,
              process_map: {
                id: currentMap?.id,
                level: currentMap?.level,
                validation_status: (currentMap as any)?.validation_status || null,
                steps: procList,
              },
            },
            verifiable_text: verifiableText.slice(0, 50000),
          },
          summary: `Process map L${currentMap?.level ?? '?'} validated in Smart Router (${procList.length} steps).`,
        };
      }}
    />
    </>
  ) : null;

  return (
    <>
      <ConfirmDialog request={confirmRequest} onClose={() => setConfirmRequest(null)} />
      {orchestratorBanner}
      <div className="space-y-4 w-full max-w-full overflow-x-hidden">
        <DownstreamStalenessBanner
          staleness={currentMap?.downstream_staleness}
          artifactLabel="process map"
          onRegenerate={() => {
            setShowLoader(true);
            setLoaderOperation('regenerating');
            setLoaderProgress(0);
            setLoaderStage('Preparing to regenerate...');
            generateMapForLevel(selectedLevel);
          }}
          isRegenerating={generating}
        />
        <div ref={canvasRef}>
          <div className="flex justify-between items-start mb-4 gap-3">
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 flex-wrap">
                <div className="flex items-center space-x-2 bg-white border border-gray-200 rounded-xl px-4 py-2 shadow-sm">
                  <h1 className="text-2xl font-bold text-super-dark-purple">Process Maps</h1>
                  <AccuracyGateBadge
                    gate={currentMap?.generation_metadata?.accuracy_gate}
                  />
                  <HelpTooltip
                    title="How Jarvis Process Mapping Works"
                    variant="default"
                    iconSize={16}
                    position="right"
                    content={
                      <div>
                        <p className="mb-2 font-medium text-gray-800">Unlike a generic AI chatbot, Jarvis:</p>
                        <ul className="list-disc pl-3 space-y-1.5">
                          <li>Synthesizes YOUR uploaded SOPs, policies, and procedures -- grounded in your actual operations</li>
                          <li>Applies BPMN 2.0 standards with proper gateway logic, swim lanes, and event categorization</li>
                          <li>Cross-references multiple document sources to resolve contradictions and fill gaps</li>
                          <li>Maintains audit trails showing which source document informed each step</li>
                          <li>Generates 4 levels of detail (Strategic to Execution) with consistent decomposition</li>
                        </ul>
                      </div>
                    }
                  />
                </div>

                {validationSteps.length > 0 && (
                  <ProcessMapValidationBar steps={validationSteps} />
                )}
              </div>
            </div>
            <div className="flex items-center space-x-2 flex-shrink-0">
              {currentProject && (
                <ClearCacheButton projectId={currentProject!.id ?? 'xyz'} />
              )}
            </div>
          </div>

          {!currentProject && (
            <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3">
              <div className="flex items-center">
                <AlertCircle className="w-4 h-4 text-yellow-600 mr-2" />
                <span className="text-sm text-yellow-800">Please select a project to generate process maps.</span>
              </div>
            </div>
          )}

        </div>
      </div>

      {showLoader && (
        <ProcessMapLoader
          selectedLevel={selectedLevel}
          operation={loaderOperation}
          processLevels={processLevels}
          progress={loaderProgress}
          stage={loaderStage}
          onCancel={loaderOperation === 'generating' || loaderOperation === 'regenerating' || loaderOperation === 'syncing' ? stopMapGeneration : undefined}
          cancelling={cancellingGeneration}
        />
      )}

      <div className="p-6 min-h-[400px] bg-gray-50">
        {loading && !showLoader ? (
          <div className="flex items-center justify-center h-96">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-electric-blue"></div>
          </div>
        ) : (
          renderProcessMapVisualization()
        )}

        {/* The Validation Report panel used to render here. The screen is the map;
            the report is now an artifact under Export → Validation Report, and the
            headline figures live on the validation bar next to Run Challenger Model. */}

        {/* Validation Command Center */}
        {currentProject && currentMap && validationId && (
          <ValidationCommandCenter
            key={`${currentProject.id}-${currentMap.id}-${validationId}`}
            isOpen={showCommandCenter}
            onClose={() => setShowCommandCenter(false)}
            processMapId={currentMap.id}
            projectId={currentProject.id}
            validationId={validationId}
            onValidationUpdate={async () => {
              // ✅ FIX: Use React Query to invalidate cache and refetch data
              await queryClient.invalidateQueries({
                queryKey: ['processMaps', currentProject.id]
              });

              // Force reload process maps with fresh data
              await loadProcessMaps();

              // Fetch updated validation status
              await fetchValidationStatus();

              // ✅ Force re-render of process map visualization
              setMapRefreshCounter(prev => prev + 1);

              toast.success('Process map updated successfully');
            }}
          />
        )}
      </div>

      {showVerificationPanel && currentMap?.generation_metadata?.verification?.metadata && (
        <div className="fixed inset-0 z-50 flex items-center justify-end bg-black/40">
          <div className="w-full max-w-xl h-full bg-white shadow-2xl overflow-y-auto">
            <div className="flex items-center justify-between px-5 py-4 border-b border-gray-200 sticky top-0 bg-white z-10">
              <h3 className="text-lg font-semibold text-gray-900">Process Map Verification</h3>
              <button
                onClick={() => setShowVerificationPanel(false)}
                className="p-1.5 rounded-md hover:bg-gray-100 text-gray-500"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="p-5">
              <VerificationPanel
                metadata={currentMap.generation_metadata.verification.metadata}
                embedded
                correctionScope="process_map"
                correctionScopeId={currentMap.id}
                onMetadataUpdated={() => loadProcessMaps()}
              />
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default ProcessMaps;